/* ============================================================
   OS9 WINDOWS — Window engine + all app launchers
   squiche.com

   Single file. Single global: window.OS9Windows.
   Cannibalized os9-window-manager.js 2026-05-24 — that file
   is now unused (kept on disk, not loaded).

   ── LOAD ORDER ──────────────────────────────────────────────
   1. squiche-os.js     — viewport lock, keyboard guard, clock
   2. os9-windows.js    — THIS FILE: engine + all app windows
   3. os9-finder.js     — finder dialog, dock icon routing
   4. os9-apps.js       — games (snake, minesweeper, flash…)
   5. app-*.js          — per-app files (paint, emulator)
   6. os9-browser-*.js  — browser window + page renderers

   ── COORDINATE SYSTEM ───────────────────────────────────────
   el.style.top / el.style.left are in #os9-desktop-area coords.
   The desktop area starts BELOW the menubar, so top:0 means
   just below the menubar — NOT viewport top.
   getDesktopBounds() → { top:0, left:0, width, height }
   where height = visualViewport.height − menubarH − statusbarH.

   ── DRAG ────────────────────────────────────────────────────
   Drag handle is .os9-win__titlebar. Moving drag to the bottom
   nav bar caused initDrag + initResize to both fire on the same
   mousedown, moving AND resizing simultaneously. Do not change.

   ── RESIZE ──────────────────────────────────────────────────
   Corner grip [data-resize] only — the OS9 size box in the bottom-right
   corner. Resizes both width and height (diagonal). Bar body is inert.
   maxW and maxH clamps prevent windows from exceeding desktop.

   ── ZOOM ────────────────────────────────────────────────────
   Standard zoom: position:fixed covering the desktop between
   menubar and statusbar. dataset.zoomed='1' marks the window.
   snapWindowsIntoView re-applies zoom geometry on every viewport
   resize so zoomed windows stay full-screen as the browser
   window is resized or the iOS URL bar appears/disappears.

   ── KEYBOARD MODE ───────────────────────────────────────────
   squiche-os.js calls enterKeyboardMode(winEl, h) when the iOS
   keyboard opens. This file saves cssText and applies a
   keyboard-sized fixed position. exitKeyboardMode restores it.
   syncKeyboardModeTop is called by handleViewport on each
   visualViewport scroll to keep the window top aligned with
   the menubar (prevents the screenshot bug where the window
   floats above the menubar when vvp.offsetTop shifts).

   ── ROADMAP 2026-05-24 ──────────────────────────────────────
   P0 — DONE: os9-window-manager.js cannibalized into this file.
        No more WM() wrapper indirection or split state.
   P0 — DONE: snapWindowsIntoView re-applies zoom geometry on
        viewport resize → zoomed windows track browser resize.
   P0 — DONE: maxW clamp in initResize — windows can't be wider
        than the desktop area.
   P0 — DONE: overflow-y:auto on .os9-win__scroll — scrollbar
        only renders when content actually overflows.
   P0 — DONE: drag on titlebar, resize on grip only (OS9 size-box corner).
   P0 — DONE: enterKeyboardMode/exitKeyboardMode/syncKeyboardModeTop
        on OS9Windows. Style save/restore guaranteed non-null.
   P0 — DONE: snapWindowsIntoView skips kbZoomed windows.
   P0 — DONE 2026-05-25: Removed _gestureActive shared mutex —
        it caused resize to fire on title-bar drag when stuck.
        Each gesture now tracks its own dragging/resizing flag.
        Added window.blur safety reset to both initDrag and
        initResize. Removed visualViewport 'scroll' listener from
        snapWindowsIntoView — it was shrinking window height
        mid-drag on iOS, making the titlebar appear to scale.
   P1 — TODO: Portrait lock overlay — @media(orientation:landscape)
        with body.flash-game-active suppression.
   P1 — TODO: Verify os9-browser-window.js blockDragIfMaximized
        guard still works with titlebar as drag handle.
   P2 — TODO: initZoom uses position:fixed + viewport coords,
        inconsistent with desktop-area coordinate system used
        by drag/resize. Not causing bugs — low urgency.
   ─────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════
     § 1  BUTTON SVGs
  ═══════════════════════════════════════════════════════════ */

  const SVG_CLOSE = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
  <rect x="1.5" y="1.5" width="10" height="10" fill="url(#wm_close)" stroke="#262626"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M2 11L3 11L3 3L11 3L11 2L3 2L2 2L2 3L2 11Z" fill="white"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M11 2H10V10H2V11H10H11V10V2Z" fill="#808080"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M13 1.07288e-06H12V12H0V13H12H13V1.07288e-06Z" fill="white"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M0 12L1 12L1 1L12 1L12 1.07288e-06L1 1.11233e-07L1.04907e-06 2.38103e-08L0 12Z" fill="#808080"/>
  <defs><linearGradient id="wm_close" x1="2" y1="2" x2="11" y2="11" gradientUnits="userSpaceOnUse"><stop stop-color="#9A9A9A"/><stop offset="1" stop-color="#F1F1F1"/></linearGradient></defs>
</svg>`;

  const SVG_ZOOM = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
  <rect x="1.5" y="1.5" width="10" height="10" fill="url(#wm_zoom)" stroke="#262626"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M2 11L3 11L3 3L11 3L11 2L3 2L2 2L2 3L2 11Z" fill="white"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M11 2H10V10H2V11H10H11V10V2Z" fill="#808080"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M13 1.07288e-06H12V12H0V13H12H13V1.07288e-06Z" fill="white"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M0 12L1 12L1 1L12 1L12 1.07288e-06L1 1.11233e-07L1.04907e-06 2.38103e-08L0 12Z" fill="#808080"/>
  <rect x="1.5" y="1.5" width="6" height="6" stroke="#262626"/>
  <defs><linearGradient id="wm_zoom" x1="2" y1="2" x2="11" y2="11" gradientUnits="userSpaceOnUse"><stop stop-color="#9A9A9A"/><stop offset="1" stop-color="#F1F1F1"/></linearGradient></defs>
</svg>`;

  const SVG_COLLAPSE = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
  <rect x="1.5" y="1.5" width="10" height="10" fill="url(#wm_col)" stroke="#262626"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M2 11L3 11L3 3L11 3L11 2L3 2L2 2L2 3L2 11Z" fill="white"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M11 2H10V10H2V11H10H11V10V2Z" fill="#808080"/>
  <rect x="1.5" y="5.5" width="10" height="2" stroke="#262626"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M13 1.07288e-06H12V12H0V13H12H13V1.07288e-06Z" fill="white"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M0 12L1 12L1 1L12 1L12 1.07288e-06L1 1.11233e-07L1.04907e-06 2.38103e-08L0 12Z" fill="#808080"/>
  <defs><linearGradient id="wm_col" x1="2" y1="2" x2="11" y2="11" gradientUnits="userSpaceOnUse"><stop stop-color="#9A9A9A"/><stop offset="1" stop-color="#F1F1F1"/></linearGradient></defs>
</svg>`;

  /* ═══════════════════════════════════════════════════════════
     § 2  IOS SCROLLBAR DETECTION
     iOS Safari ignores all ::-webkit-scrollbar-* styling.
     We feature-test by injecting a width rule and measuring
     whether a scrollable div's clientWidth is actually reduced.
     If not → use the JS-driven custom scrollbar fallback.
  ═══════════════════════════════════════════════════════════ */

  // iOS Safari doesn't support ::-webkit-scrollbar styling.
  // Detect via user-agent: iPhone/iPad/iPod, or iPad Pro (reports MacIntel but has touchPoints).
  // macOS with overlay scrollbars is NOT iOS — this avoids the false-positive from
  // feature-testing clientWidth (overlay scrollbars also have 0 scrollbar width).
  const _useJSScrollbar = (function() {
    var ua = navigator.userAgent || '';
    if (/iphone|ipad|ipod/i.test(ua)) return true;
    // iPad Pro on iOS 13+ identifies as MacIntel but has maxTouchPoints > 1
    if (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return true;
    return false;
  })();

  /* ═══════════════════════════════════════════════════════════
     § 3  STATE
  ═══════════════════════════════════════════════════════════ */

  let topZ = 1100;
  window._os9TopZ = topZ;

  var _kbModeWin        = null;
  var _kbModeSavedStyle = null;
  var _activeGestures   = 0; // count of drag/resize gestures in flight — snapWindowsIntoView skips height changes when > 0

  const windows  = new Map();
  const typeKeys = new Map();

  /* ═══════════════════════════════════════════════════════════
     § 3  DESKTOP GEOMETRY
  ═══════════════════════════════════════════════════════════ */

  let _layer;
  function getLayer() {
    if (!_layer) _layer = document.getElementById('os9-desktop-area') || document.body;
    return _layer;
  }

  function getDesktopBounds() {
    var mb  = document.getElementById('os9-menubar');
    var sb  = document.querySelector('.os9-statusbar');
    var da  = document.getElementById('os9-desktop-area');
    var mbH = mb ? mb.offsetHeight : 22;
    var sbH = sb ? sb.offsetHeight : 24;
    var vvp = window.visualViewport;
    var vH  = vvp ? vvp.height : window.innerHeight;
    var w   = da ? da.offsetWidth : ((vvp ? vvp.width : window.innerWidth) - 96);
    var h   = vH - mbH - sbH;
    return { top: 0, menubarH: mbH, left: 0, width: w, height: h };
  }

  /* ═══════════════════════════════════════════════════════════
     § 4  UTILITIES
  ═══════════════════════════════════════════════════════════ */

  let _idCounter = 0;
  function uid() { return 'oswin-' + (++_idCounter); }

  function formatMoney(cents)      { return '$' + (cents / 100).toFixed(2); }
  function formatPrice(decimalStr) { return '$' + parseFloat(decimalStr || 0).toFixed(2); }

  /* Ask Shopify's CDN for the size we actually paint.

     The product images on this store are 1920x1920 PNGs at roughly 2.7 MB
     each. Dropped straight into a 32px thumbnail that is a ~450x overdraw,
     and a collection of 28 of them pulled something like 75 MB to render a
     list of tiny squares. Passing `width` makes the CDN resize before it
     sends: the same thumbnail comes back at about 6 KB.

     Pass twice the CSS size so the image stays sharp on a 2x display. Only
     Shopify CDN URLs are rewritten; anything else is handed back untouched,
     since the parameter would just be ignored or break a signed URL. */
  function thumbUrl(src, px) {
    if (!src) return '';
    if (!/(^https?:)?\/\/cdn\.shopify\.com|\/cdn\/shop\//.test(src)) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + px;
  }

  /* Shop and cart windows used to open at the full desktop width. That reads
     as intentional edge-to-edge docking on a phone, but on a 1920px monitor
     it produced a ~1900px-wide window for a product image and two lines of
     text. Cap the width and centre it; on any viewport narrower than the cap
     this resolves to the old full-width behaviour, so mobile is unchanged. */
  /* 380 is a deliberate, narrow "utility window" width — the cart row grid
     spends 260px on its fixed columns (remove / variant / qty / price), so
     this leaves ~100px for the product name and it truncates with an ellipsis,
     which is the intended look. Widen this if names need more room. */
  const SHOP_WIN_MAX_W = 380;
  function shopWindowGeometry(bounds, x) {
    const w = Math.min(bounds.width, SHOP_WIN_MAX_W);
    return { w: w, x: x !== undefined ? x : Math.max(0, Math.round((bounds.width - w) / 2)) };
  }
  /* Product titles come from the merchant and are concatenated into row HTML.
     A title containing <, & or " would otherwise break the row it sits in. */
  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function stripTags(html) {
    const d = document.createElement('div');
    d.innerHTML = html;
    return d.textContent || '';
  }

  function focusWindow(el) {
    /* Symmetric focus state: all non-focused windows get is-background
       AND lose is-active. Previously we only removed is-active from
       others, which left them in their CSS default (which can look
       inconsistent depending on which class state is the visual baseline).
       Being explicit on both classes keeps the active/inactive distinction
       reliable regardless of CSS evolution. */
    document.querySelectorAll('.os9-win').forEach(w => {
      if (w !== el) {
        w.classList.remove('is-active');
        w.classList.add('is-background');
      }
    });
    el.classList.add('is-active');
    el.classList.remove('is-background');
    el.style.zIndex = ++topZ;
    window._os9TopZ = topZ;
  }

  /* Clicking the desktop deactivates every window, the way the Finder does —
     there is no "focused window" until you click one again. Without this a
     window stayed lit while you were plainly working somewhere else, and the
     flat inactive chrome we built never showed for the frontmost window. */
  function blurAllWindows() {
    document.querySelectorAll('.os9-win').forEach(function (w) {
      w.classList.remove('is-active');
      w.classList.add('is-background');
    });
  }

  function initDesktopBlur() {
    var layer = getLayer();
    if (!layer || layer.dataset.blurWired) return;
    layer.dataset.blurWired = '1';
    /* mousedown, not click: focusWindow() runs on mousedown too, so matching
       the phase keeps ordering predictable when the press lands on a window
       (the window's own handler re-activates it immediately afterwards). */
    function maybeBlur(e) {
      if (e.target.closest && e.target.closest('.os9-win')) return;
      blurAllWindows();
    }
    layer.addEventListener('mousedown', maybeBlur);
    layer.addEventListener('touchstart', maybeBlur, { passive: true });
  }

  function bringOrOpen(typeKey) {
    const existingId = typeKeys.get(typeKey);
    if (existingId) {
      const win = windows.get(existingId);
      if (win && document.getElementById(existingId)) { focusWindow(win.el); return true; }
      typeKeys.delete(typeKey);
    }
    return false;
  }

  function updateTrashCount(count) { if (window.OS9TrashUpdate) window.OS9TrashUpdate(count); }
  function updateCartBadge(count)  { if (window.OS9CartUpdate)  window.OS9CartUpdate(count); }

  /* ═══════════════════════════════════════════════════════════
     § 5  WINDOW CHROME
  ═══════════════════════════════════════════════════════════ */

  /* titleIcon: a URL for the small icon that sits before the window title.
     Real OS 9 puts the window's own document or volume icon there; the Apple
     glyph is the fallback for windows that have no icon of their own, and
     belongs in the menu bar rather than here. Pass a URL to override it. */
  function buildWindow({ id, title, width, height, x, y, titleIcon }) {
    const titleIconHTML = titleIcon
      ? `<img class="os9-win__title-icon" src="${titleIcon}" alt="">`
      : '<span class="os9-win__apple">&#63743;</span>';
    const el = document.createElement('div');
    el.className = 'os9-win is-active';
    el.id = id;
    el.style.cssText = `width:${width}px;height:${height}px;left:${x}px;top:${y}px;z-index:${++topZ};position:absolute;`;
    /* The size the window "wants" to be, independent of how much desktop is
       available right now. snapWindowsIntoView() shrinks toward the viewport
       and grows back toward this, so narrowing the real browser and widening
       it again returns the window to where it started rather than leaving it
       stuck at whatever the narrowest moment allowed. */
    el.dataset.desiredW = width;
    el.dataset.desiredH = height;
    window._os9TopZ = topZ;
    el.innerHTML = `
      <div class="os9-win__titlebar" data-drag>
        <button class="os9-win__btn os9-win__btn--close" data-close title="Close">${SVG_CLOSE}</button>
        <div class="os9-win__titlebar-row">
          <div class="os9-win__titlebar-stripes"></div>
          <div class="os9-win__title-group">
            ${titleIconHTML}
            <span class="os9-win__title">${title}</span>
          </div>
          <div class="os9-win__titlebar-stripes"></div>
          <button class="os9-win__btn os9-win__btn--zoom" data-zoom title="Zoom">${SVG_ZOOM}</button>
        </div>
        <button class="os9-win__btn os9-win__btn--collapse" data-minimize title="Minimize">${SVG_COLLAPSE}</button>
      </div>
      <div class="os9-win__backbar" data-backbar style="display:none">
        <button class="os9-win__back-btn" data-back>&#9664; Back</button>
        <span data-backbar-title style="font-size:var(--os9-size-ui);color:var(--os9-gray-800)"></span>
      </div>
      <div class="os9-win__chrome">
        <div class="os9-win__chrome-spacer"></div>
        <div class="os9-win__chrome-inner">
          <div class="os9-win__infobar" data-infobar></div>
          <div class="os9-win__main">
            <div class="os9-win__scroll os9-scroller" data-body></div>
            <div class="os9-jsbar" data-jsbar>
              <div class="os9-jsbar__track"><div class="os9-jsbar__thumb"></div></div>
              <button class="os9-jsbar__btn" data-jsup></button>
              <button class="os9-jsbar__btn" data-jsdn></button>
            </div>
          </div>
        </div>
        <div class="os9-win__chrome-spacer"></div>
      </div>
      <div class="os9-win__resize-edges">
        <span class="os9-win__resize-edge os9-win__resize-edge--s" data-resize-edge="s"></span>
        <span class="os9-win__resize-edge os9-win__resize-edge--e" data-resize-edge="e"></span>
        <span class="os9-win__resize-edge os9-win__resize-edge--w" data-resize-edge="w"></span>
        <span class="os9-win__resize-edge os9-win__resize-edge--se" data-resize-edge="se"></span>
        <span class="os9-win__resize-edge os9-win__resize-edge--sw" data-resize-edge="sw"></span>
      </div>`;
    syncInfobarState(el);
    return el;
  }

  function syncInfobarState(el) {
    var infobar = el.querySelector('[data-infobar]');
    if (!infobar) return;
    el.classList.toggle('os9-win--no-infobar', !infobar.textContent.trim());
  }

  function loadingHTML(msg) {
    return `<div class="os9-win__loading">
      <div>${msg || 'Loading...'}</div>
      <div class="os9-win__progress"><div class="os9-win__progress-bar"></div></div>
    </div>`;
  }

  function showDialog({ title, message, icon, buttons, onOk }) {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:9000;pointer-events:all;';
    overlay.innerHTML = `<div class="os9-dialog" style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%)">
      <div class="os9-dialog__titlebar">
        <div class="os9-dialog__titlebar-stripe"></div>
        <div class="os9-dialog__titlebar-island">${title || 'Alert'}</div>
        <div class="os9-dialog__titlebar-stripe"></div>
      </div>
      <div class="os9-dialog__body">
        <div class="os9-dialog__icon">${icon || '&#128190;'}</div>
        <div class="os9-dialog__msg">${message}</div>
      </div>
      <div class="os9-dialog__btns">
        <button class="os9-dialog__btn os9-dialog__btn--primary" data-ok>${buttons || 'OK'}</button>
      </div>
    </div>`;
    document.body.appendChild(overlay);
    overlay.querySelector('[data-ok]').addEventListener('click', () => { overlay.remove(); if (onOk) onOk(); });
  }

  /* ═══════════════════════════════════════════════════════════
     § 6  WINDOW BEHAVIORS
     initDrag — titlebar only (see DRAG note in header).
     initResize — grip ([data-resize]) only: both axes. Bar body is inert.
     initZoom — standard zoom tracks viewport via snapWindowsIntoView.
  ═══════════════════════════════════════════════════════════ */

  function initDrag(el) {
    const titlebar = el.querySelector('.os9-win__titlebar');
    if (!titlebar) return;
    let startX, startY, startLeft, startTop, dragging = false;
    const DRAG_THRESHOLD = 3; /* px before a drag is considered "moved" */
    function onStart(cx, cy) {
      if (el.dataset.zoomed || el.dataset.kbZoomed) return;
      dragging = true;
      el._dragMoved = false; /* read by the titlebar click handler in initMinimize
                                to suppress the synthetic click that fires after
                                mouseup ends a real drag */
      _activeGestures++;
      focusWindow(el);
      startX = cx; startY = cy; startLeft = el.offsetLeft; startTop = el.offsetTop;
    }
    function onMove(cx, cy) {
      if (!dragging || el.dataset.kbZoomed) return;
      if (!el._dragMoved && (Math.abs(cx - startX) > DRAG_THRESHOLD || Math.abs(cy - startY) > DRAG_THRESHOLD)) {
        el._dragMoved = true;
      }
      const bounds  = getDesktopBounds();
      const minLeft = -(el.offsetWidth - 60);
      const maxLeft = bounds.width - 60;
      el.style.left = Math.min(maxLeft, Math.max(minLeft, startLeft + cx - startX)) + 'px';
      el.style.top  = Math.max(0, startTop + cy - startY) + 'px';
    }
    /* Document listeners live only for the duration of a drag.

       They used to be registered permanently, once per window. A non-passive
       document 'touchmove' makes iOS wait for the handler before it can
       scroll anything — so every window ever opened added another handler to
       run before every single scroll gesture, anywhere on the page. Binding
       on demand means zero cost when nothing is being dragged. */
    function onDocMouseMove(e) { if (!dragging) return; onMove(e.clientX, e.clientY); }
    function onDocTouchMove(e) {
      if (!dragging) return;
      e.preventDefault();
      const t = e.touches[0]; if (t) onMove(t.clientX, t.clientY);
    }
    function onEnd() {
      if (!dragging) return;
      dragging = false;
      _activeGestures = Math.max(0, _activeGestures - 1);
      document.removeEventListener('mousemove', onDocMouseMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onDocTouchMove, { passive: false });
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
      window.removeEventListener('blur', onEnd);
    }
    function bindDrag() {
      document.addEventListener('mousemove', onDocMouseMove);
      document.addEventListener('mouseup', onEnd);
      document.addEventListener('touchmove', onDocTouchMove, { passive: false });
      document.addEventListener('touchend', onEnd);
      document.addEventListener('touchcancel', onEnd);
      window.addEventListener('blur', onEnd);   /* safety: focus lost mid-drag */
    }
    titlebar.addEventListener('mousedown',  e => { if (e.target.closest('button')) return; onStart(e.clientX, e.clientY); bindDrag(); e.preventDefault(); e.stopPropagation(); });
    titlebar.addEventListener('touchstart', e => { const t = e.touches[0]; if (!t || e.target.closest('button')) return; onStart(t.clientX, t.clientY); bindDrag(); e.preventDefault(); e.stopPropagation(); }, { passive: false });
  }

  function initResize(el) {
    var bindResize = function () {};
    /* Multi-edge resize. The window has 8 invisible handles around its
       perimeter (4 edges + 4 corners), each with a data-resize-edge
       attribute indicating which sides to grow/shrink. Replaces the
       previous single-corner grabber + bottom-nav bar. Resize is clamped
       to desktop bounds and MIN_W × MIN_H. */
    const handles = el.querySelectorAll('[data-resize-edge]');
    if (!handles.length) return;

    const MIN_W = WIN_MIN_W, MIN_H = WIN_MIN_H;
    let activeEdge = null;
    let startX, startY, startW, startH, startLeft, startTop;

    function start(cx, cy, edge) {
      if (el.dataset.zoomed || el.dataset.fullscreenZoom || el.dataset.mobileContact || el.dataset.kbZoomed) return;
      activeEdge = edge;
      _activeGestures++;
      focusWindow(el);
      startX = cx; startY = cy;
      startW = el.offsetWidth; startH = el.offsetHeight;
      startLeft = el.offsetLeft; startTop = el.offsetTop;
    }
    function move(cx, cy) {
      if (!activeEdge) return;
      const bounds = getDesktopBounds();
      const dx = cx - startX, dy = cy - startY;
      let newW = startW, newH = startH, newL = startLeft, newT = startTop;

      if (activeEdge.indexOf('e') !== -1) {
        newW = Math.max(MIN_W, Math.min(bounds.width - startLeft, startW + dx));
      }
      if (activeEdge.indexOf('w') !== -1) {
        const rightEdge = startLeft + startW;
        newL = Math.max(0, Math.min(rightEdge - MIN_W, startLeft + dx));
        newW = rightEdge - newL;
      }
      if (activeEdge.indexOf('s') !== -1) {
        newH = Math.max(MIN_H, Math.min(bounds.height - startTop, startH + dy));
      }
      if (activeEdge.indexOf('n') !== -1) {
        const bottomEdge = startTop + startH;
        newT = Math.max(0, Math.min(bottomEdge - MIN_H, startTop + dy));
        newH = bottomEdge - newT;
      }

      el.style.left   = newL + 'px';
      el.style.top    = newT + 'px';
      el.style.width  = newW + 'px';
      el.style.height = newH + 'px';
    }
    function end() {
      if (activeEdge) {
        activeEdge = null;
        _activeGestures = Math.max(0, _activeGestures - 1);
        /* A size the user chose by hand becomes the new size to grow back to. */
        el.dataset.desiredW = el.offsetWidth;
        el.dataset.desiredH = el.offsetHeight;
      }
    }

    handles.forEach(function (handle) {
      const edge = handle.dataset.resizeEdge;
      handle.addEventListener('mousedown', function (e) {
        start(e.clientX, e.clientY, edge); bindResize();
        e.preventDefault(); e.stopPropagation();
      });
      handle.addEventListener('touchstart', function (e) {
        const t = e.touches[0]; if (!t) return;
        start(t.clientX, t.clientY, edge); bindResize();
        e.preventDefault(); e.stopPropagation();
      }, { passive: false });
    });

    /* Same on-demand binding as initDrag — see the note there. A permanent
       non-passive document 'touchmove' per window taxes every scroll on iOS. */
    function onDocMouseMove(e) { if (!activeEdge) return; move(e.clientX, e.clientY); }
    function onDocTouchMove(e) {
      if (!activeEdge) return;
      const t = e.touches[0]; if (!t) return;
      move(t.clientX, t.clientY); e.preventDefault();
    }
    function unbindResize() {
      document.removeEventListener('mousemove', onDocMouseMove);
      document.removeEventListener('mouseup', endAndUnbind);
      document.removeEventListener('touchmove', onDocTouchMove, { passive: false });
      document.removeEventListener('touchend', endAndUnbind);
      document.removeEventListener('touchcancel', endAndUnbind);
      window.removeEventListener('blur', endAndUnbind);
    }
    function endAndUnbind() { end(); unbindResize(); }
    bindResize = function () {
      document.addEventListener('mousemove', onDocMouseMove);
      document.addEventListener('mouseup', endAndUnbind);
      document.addEventListener('touchmove', onDocTouchMove, { passive: false });
      document.addEventListener('touchend', endAndUnbind);
      document.addEventListener('touchcancel', endAndUnbind);
      window.addEventListener('blur', endAndUnbind);
    };
  }

  function initMinimize(el) {
    const btn      = el.querySelector('[data-minimize]');
    const titlebar = el.querySelector('[data-drag]');
    let minimized = false, savedHeight;
    btn.addEventListener('click', e => {
      e.stopPropagation();
      /* Hard guard against the conflicting state. Per the OS9 reference
         and user spec: when zoomed, you can't minimize until you un-zoom
         first via the zoom button. When minimized, you can't zoom (see
         the matching guard in initZoom). This avoids the savedStyle
         snapshot corruption the previous cascade implementation produced. */
      if (!minimized && (el.dataset.zoomed === '1' || el.dataset.fullscreenZoom === '1')) {
        return; /* user must click zoom first to restore */
      }
      const parts = [
        el.querySelector('.os9-win__chrome'),
        el.querySelector('.os9-win__resize-edges'),
        el.querySelector('[data-backbar]'),
      ].filter(Boolean);
      if (!minimized) {
        savedHeight = el.style.height || (el.offsetHeight + 'px');
        parts.forEach(p => { p.dataset.wasVisible = p.style.display !== 'none' ? '1' : ''; p.style.display = 'none'; });
        Object.assign(el.style, { height: 'auto', background: 'transparent', boxShadow: 'none', border: 'none', pointerEvents: 'none' });
        titlebar.style.pointerEvents = 'auto'; btn.style.pointerEvents = 'auto';
        minimized = true;
        el.dataset.minimized = '1';
      } else {
        parts.forEach(p => { if (p.dataset.wasVisible !== '') p.style.display = ''; });
        // Use pre-keyboard height when restoring after the keyboard has already closed,
        // so the window doesn't inherit the keyboard-mode fullscreen height.
        var heightToRestore = (!el.dataset.kbZoomed && el.dataset.kbSavedHeight)
          ? el.dataset.kbSavedHeight : savedHeight;
        Object.assign(el.style, { height: heightToRestore, background: '', boxShadow: '', border: '', pointerEvents: '' });
        titlebar.style.pointerEvents = ''; btn.style.pointerEvents = '';
        minimized = false;
        delete el.dataset.minimized;
        focusWindow(el);
        // Skip top-clamping for mobileContact (fixed geometry) and kbZoomed (keyboard mode
        // manages position; clamping with the shrunken bnds.height produces negative maxTop
        // which forces top:0, hiding the titlebar behind the menubar).
        if (!el.dataset.mobileContact && !el.dataset.kbZoomed) {
          var bnds = getDesktopBounds();
          var maxTop = bnds.height - el.offsetHeight;
          if (el.offsetTop > maxTop) el.style.top = Math.max(0, maxTop) + 'px';
        }
      }
    });
    titlebar.addEventListener('click', e => {
      /* Drag-on-titlebar fires a synthetic click on mouseup after the drag
         ends. Without this guard, ending a drag on a minimized window's
         titlebar restored the window. el._dragMoved is set by initDrag's
         onMove once movement crosses DRAG_THRESHOLD; we reset it here so
         a true click immediately afterward still works. */
      if (el._dragMoved) { el._dragMoved = false; return; }
      if (!minimized || e.target.closest('button')) return;
      btn.click();
    });
  }

  function initClose(el, id, typeKey) {
    el.querySelector('[data-close]').addEventListener('click', () => {
      if (el._mobileContactResizeHandler && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', el._mobileContactResizeHandler);
      }
      el.remove();
      windows.delete(id);
      if (typeKey) typeKeys.delete(typeKey);
    });
  }

  function initZoom(el) {
    const btn = el.querySelector('[data-zoom]');
    let savedStyle = null;
    btn.addEventListener('click', () => {
      if (el.dataset.mobileContact) return;
      /* Hard guard — can't zoom while minimized. User must un-minimize
         first by clicking the minimize button (which is still active and
         draggable in the minimized state). This matches the OS9 reference
         behavior and avoids the savedStyle snapshot corruption that
         cascading the operations produced. */
      if (el.dataset.minimized === '1') return;
      if (el.dataset.fullscreenZoom) {
        if (savedStyle) { el.style.cssText = savedStyle; savedStyle = null; }
        else { savedStyle = el.style.cssText; el.style.left = '0px'; el.style.width = (getLayer().offsetWidth || window.innerWidth) + 'px'; el.style.zIndex = 99999; }
        return;
      }
      if (savedStyle) {
        el.style.cssText = savedStyle;
        savedStyle = null;
        delete el.dataset.zoomed;
      } else {
        savedStyle = el.style.cssText;
        const root = document.querySelector('.os9-section-root');
        const mb   = document.getElementById('os9-menubar');
        const sb   = document.querySelector('.os9-statusbar');
        const mbH  = mb  ? mb.offsetHeight  : 22;
        const sbH  = sb  ? sb.offsetHeight  : 24;
        const rW   = root ? root.offsetWidth  : window.innerWidth;
        const rH   = root ? root.offsetHeight : window.innerHeight;
        Object.assign(el.style, {
          position: 'fixed', left: '0', top: mbH + 'px',
          width: rW + 'px', height: (rH - mbH - sbH) + 'px', zIndex: '99998',
        });
        el.dataset.zoomed = '1';
      }
    });
  }

  function watchScrollable(scrollEl) {
    if (!scrollEl) return;

    /* One class carries the whole OS9 scrollbar treatment. The chrome used to
       be written three times over — .os9-win__scroll, #sqv-lb-body and
       [id^="os9-page-"] listed in every rule — which is how one family drifted
       out of sync and the browser's bars silently stopped greying. The three
       creation sites stamp this in their markup so there is no unstyled frame;
       adding it here too means any future scroller gets the chrome for free. */
    scrollEl.classList.add('os9-scroller');

    const jsbar  = scrollEl.parentElement && scrollEl.parentElement.querySelector('[data-jsbar]');
    const jtrack = jsbar && jsbar.querySelector('.os9-jsbar__track');
    const jthumb = jsbar && jsbar.querySelector('.os9-jsbar__thumb');

    function thumbMetrics() {
      const trackH    = jtrack ? jtrack.clientHeight : 0;
      const ratio     = scrollEl.scrollHeight > 0 ? scrollEl.clientHeight / scrollEl.scrollHeight : 1;
      const thumbH    = Math.max(20, ratio * trackH);
      const maxTop    = trackH - thumbH;
      const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
      return { trackH, thumbH, maxTop, maxScroll };
    }

    function positionThumb() {
      if (!jthumb || !jtrack) return;
      const { thumbH, maxTop, maxScroll } = thumbMetrics();
      const top = maxScroll > 0 ? (scrollEl.scrollTop / maxScroll) * maxTop : 0;
      jthumb.style.height = thumbH + 'px';
      jthumb.style.top    = top    + 'px';
    }

    function check() {
      const overflow = scrollEl.scrollHeight > scrollEl.clientHeight;
      scrollEl.classList.toggle('is-scrollable', overflow);
      /* Same test on the other axis. The bars are always drawn now, so the
         arrows need to know when they have nothing to do — OS 9 greys them
         out and flattens them rather than hiding them. */
      scrollEl.classList.toggle('is-hscrollable',
        scrollEl.scrollWidth > scrollEl.clientWidth);
      if (_useJSScrollbar && jsbar) {
        jsbar.style.display = overflow ? 'flex' : 'none';
        if (overflow) positionThumb();
      }
    }

    check();
    /* The observers below cover content that changes after this point, but on a
       cold open the very first layout has not settled when watchScrollable runs
       — the lightbox measures 433 of content in a 433 box, then the artwork
       decodes and it becomes 760. Re-checking on the next frame and twice more
       shortly after costs nothing and makes the initial state deterministic
       rather than dependent on which observer happens to fire first. */
    scrollEl._os9Recheck = check;
    requestAnimationFrame(check);
    setTimeout(check, 150);
    setTimeout(check, 600);

    /* Why this is more than one observer: a scroller's own box does NOT change
       when its content grows, so a ResizeObserver on scrollEl alone never fires
       for the case that matters — content getting taller. The children have to
       be observed too, and re-observed as they are added. And an <img> finishing
       its decode changes layout without mutating the DOM at all, which is why
       the capture-phase 'load' listener is here: image load events don't bubble,
       but they do capture. Missing any one of these three left the lightbox
       reporting "not scrollable" while sitting on 760px of content in a 433px
       box, so its arrows stayed greyed and it looked unlike every other bar. */
    var ro = window.ResizeObserver ? new ResizeObserver(check) : null;
    function observeContent() {
      if (!ro) return;
      ro.observe(scrollEl);
      for (var i = 0; i < scrollEl.children.length; i++) ro.observe(scrollEl.children[i]);
    }
    observeContent();
    new MutationObserver(function() {
      observeContent();                       /* observe() is idempotent */
      requestAnimationFrame(check);
    }).observe(scrollEl, { childList: true, subtree: true });
    scrollEl.addEventListener('load', function() {
      requestAnimationFrame(check);
    }, true);
    scrollEl.addEventListener('scroll', function() {
      if (_useJSScrollbar) positionThumb();
    }, { passive: true });

    if (!_useJSScrollbar || !jsbar) return; // desktop: CSS scrollbar handles everything

    /* ── JS scrollbar interaction (iOS) ── */
    const upBtn = jsbar.querySelector('[data-jsup]');
    const dnBtn = jsbar.querySelector('[data-jsdn]');
    if (upBtn) upBtn.addEventListener('click', () => { scrollEl.scrollTop -= 40; });
    if (dnBtn) dnBtn.addEventListener('click', () => { scrollEl.scrollTop += 40; });

    // Track click: jump to clicked position
    if (jtrack) {
      jtrack.addEventListener('click', function(e) {
        if (e.target === jthumb) return;
        const rect = jtrack.getBoundingClientRect();
        const frac = (e.clientY - rect.top) / jtrack.clientHeight;
        scrollEl.scrollTop = frac * scrollEl.scrollHeight - scrollEl.clientHeight / 2;
      });
    }

    // Thumb drag (touch + mouse)
    if (jthumb) {
      let dragging = false, startY = 0, startScroll = 0;

      function startDrag(clientY) {
        dragging = true; startY = clientY; startScroll = scrollEl.scrollTop;
      }
      function moveDrag(clientY) {
        if (!dragging) return;
        const { maxTop, maxScroll } = thumbMetrics();
        if (maxTop <= 0) return;
        const delta = (clientY - startY) * maxScroll / maxTop;
        scrollEl.scrollTop = Math.max(0, Math.min(maxScroll, startScroll + delta));
      }
      function endDrag() { dragging = false; }

      /* Document listeners are attached only for the duration of a drag, and
         torn down when it ends.

         Previously they were registered permanently, once per scrollable
         window. Two problems came with that:

           1. A non-passive document 'touchmove' forces iOS to wait for the
              handler before it can scroll ANYTHING, on every touch — a global
              scrolling tax paid even when no drag is in progress, multiplied
              by every window that had ever been opened.
           2. Only 'touchend' cleared `dragging`. iOS fires 'touchcancel'
              instead whenever the system takes over a gesture, which left
              `dragging` stuck true — and from then on every touchmove hit
              preventDefault(), killing scrolling entirely until a reload.

         Binding on demand fixes both, and 'touchcancel' is now handled. */
      function onTouchMove(e) {
        if (!dragging) return;
        moveDrag(e.touches[0].clientY);
        e.preventDefault();
      }
      function onMouseMove(e) { if (dragging) moveDrag(e.clientY); }
      function releaseDrag() {
        if (!dragging) return;
        dragging = false;
        document.removeEventListener('touchmove', onTouchMove, { passive: false });
        document.removeEventListener('touchend', releaseDrag);
        document.removeEventListener('touchcancel', releaseDrag);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', releaseDrag);
      }
      function beginDrag(clientY) {
        startDrag(clientY);
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', releaseDrag, { passive: true });
        document.addEventListener('touchcancel', releaseDrag, { passive: true });
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', releaseDrag);
      }

      jthumb.addEventListener('touchstart', function(e) {
        beginDrag(e.touches[0].clientY); e.preventDefault(); e.stopPropagation();
      }, { passive: false });
      jthumb.addEventListener('mousedown', function(e) { beginDrag(e.clientY); e.preventDefault(); });
    }
  }

  function _initWindow(el, id, opts) {
    opts = opts || {};
    windows.set(id, { el });
    initDrag(el); initResize(el); initMinimize(el); initClose(el, id); initZoom(el);
    el.addEventListener('mousedown',  () => focusWindow(el));
    el.addEventListener('touchstart', () => focusWindow(el), { passive: true });
    focusWindow(el);
    watchScrollable(el.querySelector('.os9-win__scroll'));
    if (opts.mobileFullscreen && window.innerWidth <= 749) {
      var zoomBtn = el.querySelector('[data-zoom]');
      if (zoomBtn) zoomBtn.click();
    }
  }

  /* ═══════════════════════════════════════════════════════════
     § 7  KEYBOARD MODE
     squiche-os.js delegates all window geometry changes here.
     _kbModeSavedStyle is guaranteed non-null at exitKeyboardMode
     time because enterKeyboardMode always sets it first.
  ═══════════════════════════════════════════════════════════ */

  function enterKeyboardMode(winEl, availableHeight) {
    if (!winEl || winEl.dataset.zoomed || winEl.dataset.fullscreenZoom || winEl.dataset.kbZoomed || winEl.dataset.mobileContact) return false;
    _kbModeWin        = winEl;
    _kbModeSavedStyle = winEl.style.cssText;
    winEl.dataset.kbSavedHeight = winEl.style.height || (winEl.offsetHeight + 'px');
    var vvp       = window.visualViewport;
    var mb        = document.getElementById('os9-menubar');
    var mbH       = mb ? mb.offsetHeight : 22;
    var rW        = vvp ? vvp.width  : window.innerWidth;
    var offsetTop = vvp ? vvp.offsetTop : 0;
    Object.assign(winEl.style, {
      position: 'fixed', left: '0px', top: (offsetTop + mbH) + 'px',
      width: rW + 'px', height: availableHeight + 'px', zIndex: '99998',
    });
    winEl.dataset.kbZoomed = '1';
    return true;
  }

  function exitKeyboardMode() {
    if (!_kbModeWin) return;
    if (_kbModeSavedStyle !== null) _kbModeWin.style.cssText = _kbModeSavedStyle;
    delete _kbModeWin.dataset.kbZoomed;
    _kbModeWin = null; _kbModeSavedStyle = null;
  }

  function syncKeyboardModeTop(topPx) {
    if (_kbModeWin) _kbModeWin.style.top = topPx + 'px';
  }

  function syncMobileContactTop(topPx) {
    document.querySelectorAll('.os9-win[data-mobile-contact]').forEach(function (win) {
      win.style.top = topPx + 'px';
    });
  }

  /* ═══════════════════════════════════════════════════════════
     § 8  VIEWPORT MANAGEMENT
     snapWindowsIntoView fires on every resize/scroll event.
     — Snaps normal windows back into the desktop area.
     — Re-applies zoom geometry for zoomed windows so they stay
       full-screen when the browser window is resized.
     — Skips kbZoomed windows (keyboard guard owns their sizing).
  ═══════════════════════════════════════════════════════════ */

  /* Shared with the resize grabber, which clamps to the same floor. */
  var WIN_MIN_W = 220, WIN_MIN_H = 160;

  function snapWindowsIntoView() {
    var bounds = getDesktopBounds();
    var root   = document.querySelector('.os9-section-root');
    var mb     = document.getElementById('os9-menubar');
    var sb     = document.querySelector('.os9-statusbar');
    var mbH    = mb ? mb.offsetHeight : 22;
    var sbH    = sb ? sb.offsetHeight : 24;
    var rW     = root ? root.offsetWidth  : window.innerWidth;
    var rH     = root ? root.offsetHeight : window.innerHeight;

    document.querySelectorAll('.os9-win').forEach(function (win) {
      if (win.dataset.kbZoomed || win.dataset.mobileContact) return;

      if (win.dataset.zoomed) {
        Object.assign(win.style, {
          position: 'fixed', left: '0', top: mbH + 'px',
          width: rW + 'px', height: (rH - mbH - sbH) + 'px',
        });
        return;
      }

      // Skip re-sizing during an active drag/resize — iOS can fire resize events
      // mid-gesture (URL bar show/hide), which would visually scale the window while
      // the user is dragging it. _activeGestures is > 0 whenever drag or resize is live.
      if (_activeGestures === 0 && !win.dataset.fullscreenZoom) {
        /* Track the real browser in BOTH axes. Clamping only `left` would push
           an over-wide window leftward while it still ran off the right edge,
           and shrinking without a remembered desired size meant widening the
           browser back left the window stuck small. */
        var desiredW = parseFloat(win.dataset.desiredW) || win.offsetWidth;
        var desiredH = parseFloat(win.dataset.desiredH) || win.offsetHeight;

        var newW = Math.min(desiredW, Math.max(WIN_MIN_W, bounds.width - 8));
        if (Math.abs(newW - win.offsetWidth) >= 1) win.style.width = newW + 'px';

        /* Measure the height budget from the top the window will actually have:
           pulling it up off the bottom edge frees room, so lift first. */
        var fitH = Math.min(desiredH, Math.max(WIN_MIN_H, bounds.height));
        if (win.offsetTop > 0 && win.offsetTop + fitH > bounds.height) {
          win.style.top = Math.max(0, bounds.height - fitH) + 'px';
        }
        var newH = Math.min(desiredH, Math.max(WIN_MIN_H, bounds.height - win.offsetTop));
        if (Math.abs(newH - win.offsetHeight) >= 1) win.style.height = newH + 'px';
      }

      var currentTop  = win.offsetTop;
      var currentLeft = win.offsetLeft;
      var minLeft     = -(win.offsetWidth - 60);
      var maxLeft     = bounds.width - 60;
      if (currentTop  < 0)       win.style.top  = '0px';
      if (currentLeft < minLeft) win.style.left = minLeft + 'px';
      if (currentLeft > maxLeft) win.style.left = maxLeft + 'px';
      /* Once the window fits again, pull it fully back on screen rather than
         leaving it hanging off the right edge from a narrower moment. */
      if (win.offsetWidth <= bounds.width && win.offsetLeft + win.offsetWidth > bounds.width) {
        win.style.left = Math.max(0, bounds.width - win.offsetWidth) + 'px';
      }
    });
  }

  window.addEventListener('resize', snapWindowsIntoView);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', snapWindowsIntoView);
    // NOTE: visualViewport 'scroll' intentionally omitted.
    // On iOS, a touch-drag of the title bar can fire visualViewport scroll,
    // which calls snapWindowsIntoView and shrinks the window height mid-drag —
    // making it appear the titlebar is scaling the window. The 'resize' event
    // above is sufficient to re-apply zoom geometry when the URL bar appears/hides.
  }

  /* ═══════════════════════════════════════════════════════════
     § 9  SHOPIFY API
  ═══════════════════════════════════════════════════════════ */

  /* ────────────────────────────────────────────────────────────
     SHOPIFY API
  ──────────────────────────────────────────────────────────── */

  async function fetchCollection(handle) {
    /* Cached for the life of the page, the same way products are. Without this
       every Back out of a product refetched the whole collection, and the frame
       rendered empty until the response landed — which is exactly the header
       blinking out and coming back. A collection's contents don't change
       mid-session, so a miss only ever happens on the first visit. */
    if (_collectionCache[handle]) return _collectionCache[handle];
    var p = fetch('/collections/' + handle + '/products.json?limit=50')
      .then(function (res) {
        if (!res.ok) throw new Error('Failed');
        return res.json();
      })
      .then(function (j) { return j.products; });
    /* A rejected promise must not poison the cache — a first load that failed
       on a flaky connection has to stay retryable. */
    p.catch(function () { delete _collectionCache[handle]; });
    _collectionCache[handle] = p;
    return p;
  }

  /* Uses /products/<handle>.js, NOT .json — the .json variant payload has no
     `available` field at all, so `v.available` read as undefined on every
     size. Undefined is falsy, so the picker labelled every size "— Sold Out"
     and set option.disabled, which made it impossible to choose one and
     Add to Cart always failed with "Please select a size." Every tee was
     unbuyable through this UI regardless of real stock.

     The .js endpoint carries `available`, but differs in three ways, all
     normalised here so callers stay unchanged:
       price        integer cents  → decimal string, for formatPrice()
       description                 → body_html
       featured_image / images     already plain URL strings; renderProductDetail
                                     already handles both string and object. */
  async function fetchProduct(handle) {
    /* Warmed on collection expand; falls through to the network on a miss. */
    let p = _productCache[handle] ? await _productCache[handle] : null;
    if (!p) {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) throw new Error('Failed');
      p = await res.json();
      _productCache[handle] = Promise.resolve(p);
    }
    return Object.assign({}, p, {
      body_html: p.description,
      variants: (p.variants || []).map(function (v) {
        return Object.assign({}, v, { price: (v.price / 100).toFixed(2) });
      }),
    });
  }

  async function addToCart(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
    });
    if (!res.ok) throw new Error('Failed');
    return res.json();
  }

  async function fetchCart() {
    return (await fetch('/cart.js')).json();
  }

  /* ────────────────────────────────────────────────────────────
     OS9 CUSTOM SELECT WIDGET
  ──────────────────────────────────────────────────────────── */

  function OS9Select(selectEl) {
    if (!selectEl || selectEl.dataset.os9select) return;
    selectEl.dataset.os9select = '1';
    selectEl.classList.add('os9-select-hidden');

    const wrapper  = document.createElement('div');  wrapper.className  = 'os9-select'; wrapper.style.flex = '1';
    const trigger  = document.createElement('div');  trigger.className  = 'os9-select__trigger';
    const sep      = document.createElement('div');  sep.className      = 'os9-select__sep';
    const btn      = document.createElement('div');  btn.className      = 'os9-select__btn';
    const dropdown = document.createElement('div');  dropdown.className = 'os9-select__dropdown';

    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="8" height="13" viewBox="0 0 8 13" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 2.5H3.5V3H3V3.5H2.5V4H2V4.5H1.5V5H6.5V4.5H6V4H5.5V3.5H5V3H4.5V2.5Z" fill="#262626"/><path fill-rule="evenodd" clip-rule="evenodd" d="M3.5 10L4.5 10L4.5 9.5L5 9.5L5 9L5.5 9L5.5 8.5L6 8.5L6 8L6.5 8L6.5 7.5L1.5 7.5L1.5 8L2 8L2 8.5L2.5 8.5L2.5 9L3 9L3 9.5L3.5 9.5L3.5 10Z" fill="#262626"/></svg>';
    trigger.textContent = selectEl.options[selectEl.selectedIndex]?.text || '';
    /* Exposed so external code can resync the trigger after programmatic option changes */
    selectEl._os9Refresh = function () {
      trigger.textContent = selectEl.options[selectEl.selectedIndex]?.text || '';
    };

    function renderOptions() {
      dropdown.innerHTML = '';
      Array.from(selectEl.options).forEach(function (opt, i) {
        const item = document.createElement('div');
        item.className = 'os9-select__option' + (selectEl.selectedIndex === i ? ' is-selected' : '');
        item.textContent = opt.text;
        function pickOption(e) {
          e.preventDefault(); e.stopPropagation();
          selectEl.selectedIndex = i;
          selectEl.dispatchEvent(new Event('change', { bubbles: true }));
          trigger.textContent = opt.text;
          close();
        }
        item.addEventListener('mousedown', pickOption);
        item.addEventListener('touchend',  pickOption);
        dropdown.appendChild(item);
      });
    }
    /* Mount point: the OS9 root, not document.body.
       Appending to body takes the dropdown outside the locked
       position:fixed container and causes iOS to re-evaluate the
       visual viewport, which pans the menubar out of position. */
    var _mount = document.querySelector('.os9-section-root') || document.body;

    function open() {
      renderOptions();
      const rect = wrapper.getBoundingClientRect();
      dropdown.style.cssText = 'position:fixed;z-index:999999;left:' + rect.left + 'px;top:' + rect.bottom + 'px;width:' + rect.width + 'px;';
      _mount.appendChild(dropdown);
      trigger.classList.add('is-open'); btn.classList.add('is-open'); dropdown.classList.add('is-open');
      setTimeout(function () {
        document.addEventListener('mousedown', close, { once: true });
        document.addEventListener('touchend',  close, { once: true });
      }, 0);
    }
    function close() {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchend',  close);
      trigger.classList.remove('is-open'); btn.classList.remove('is-open'); dropdown.classList.remove('is-open');
      if (dropdown.parentNode === _mount) { dropdown.style.cssText = ''; wrapper.appendChild(dropdown); }
    }
    function toggle(e) {
      e.preventDefault(); e.stopPropagation();
      dropdown.classList.contains('is-open') ? close() : open();
    }
    trigger.addEventListener('mousedown', toggle);
    trigger.addEventListener('touchend',  toggle);
    btn.addEventListener('mousedown', toggle);
    btn.addEventListener('touchend',  toggle);
    selectEl.parentNode.insertBefore(wrapper, selectEl);
    wrapper.appendChild(trigger); wrapper.appendChild(sep); wrapper.appendChild(btn); wrapper.appendChild(dropdown); wrapper.appendChild(selectEl);
  }
  window.OS9Select = OS9Select;

  /* ────────────────────────────────────────────────────────────
     HSV COLOR PICKER
  ──────────────────────────────────────────────────────────── */

  var PILL_SVG = '<svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.7693 1.23077L14.7693 12.9231L8.00006 17.8462L1.23083 12.3077L1.23083 1.23077L14.7693 1.23077Z" fill="#8B8BDC"/><rect x="16" y="0.617188" width="0.617" height="0.615" transform="rotate(90 16 0.617188)" fill="#B4B4B4"/><rect x="15.3845" y="0.617188" width="0.617" height="0.615" transform="rotate(90 15.3845 0.617188)" fill="#8B8B8B"/><rect x="14.7693" y="0.617188" width="0.617" height="0.615" transform="rotate(90 14.7693 0.617188)" fill="#565656"/><rect x="15.3845" y="0" width="0.617" height="0.615" transform="rotate(90 15.3845 0)" fill="#B4B4B4"/><rect x="14.7693" y="0" width="0.617" height="0.615" transform="rotate(90 14.7693 0)" fill="#616161"/><rect x="16" y="1.23077" width="0.617" height="0.615" transform="rotate(90 16 1.23077)" fill="#616161"/><rect x="15.3845" y="1.23077" width="0.617" height="0.615" transform="rotate(90 15.3845 1.23077)" fill="#565656"/><rect x="14.7693" y="1.23077" width="0.617" height="0.615" transform="rotate(90 14.7693 1.23077)" fill="#5E5E96"/><rect x="14.1538" y="1.23077" width="0.617" height="0.615" transform="rotate(90 14.1538 1.23077)" fill="#7878B2"/><rect x="13.5386" y="1.23077" width="0.615" height="11.6923" transform="rotate(90 13.5386 1.23077)" fill="#8C8CB3"/><rect x="14.1538" y="0" width="0.617" height="12.3077" transform="rotate(90 14.1538 0)" fill="#262626"/><rect x="14.1538" y="0.617188" width="0.617" height="12.3077" transform="rotate(90 14.1538 0.617188)" fill="#262626"/><rect x="13.5386" y="1.84613" width="0.615" height="11.0769" transform="rotate(90 13.5386 1.84613)" fill="#9C9CDD"/><rect x="16" y="1.84613" width="9.23077" height="1.23077" transform="rotate(90 16 1.84613)" fill="#262626"/><rect x="1.23071" y="1.84613" width="9.23077" height="1.23077" transform="rotate(90 1.23071 1.84613)" fill="#262626"/><rect x="14.7693" y="2.46155" width="8.61539" height="0.615384" transform="rotate(90 14.7693 2.46155)" fill="#494990"/><rect x="14.1538" y="2.46155" width="8.61539" height="0.615384" transform="rotate(90 14.1538 2.46155)" fill="#6C6CC4"/><rect x="2.46143" y="2.46155" width="8.61539" height="0.615383" transform="rotate(90 2.46143 2.46155)" fill="#A1A1DD"/><rect x="1.84619" y="2.46155" width="8.61539" height="0.615384" transform="rotate(90 1.84619 2.46155)" fill="#8484A7"/><rect x="6.15381" y="6.15381" width="4.30769" height="0.615384" transform="rotate(90 6.15381 6.15381)" fill="#4D4D9A"/><rect x="5.53833" y="6.15381" width="4.30769" height="0.615383" transform="rotate(90 5.53833 6.15381)" fill="#8989C3"/><rect x="4.9231" y="6.15381" width="4.30769" height="0.615385" transform="rotate(90 4.9231 6.15381)" fill="#A8A8DB"/><rect x="8.61548" y="6.15381" width="4.30769" height="0.615384" transform="rotate(90 8.61548 6.15381)" fill="#4D4D9A"/><rect x="8" y="6.15381" width="4.30769" height="0.615383" transform="rotate(90 8 6.15381)" fill="#8989C3"/><rect x="7.38477" y="6.15381" width="4.30769" height="0.615384" transform="rotate(90 7.38477 6.15381)" fill="#A8A8DB"/><path d="M8.00006 17.8462L1.23083 12.3077V13.5385L8.00006 19.0769L14.7693 13.5385V12.3077L8.00006 17.8462Z" fill="#494990"/></svg>';
  var PILL_URL = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(PILL_SVG);

  function hsvToHex(h, s, v) {
    var r, g, b, i = Math.floor(h / 60) % 6, f = h / 60 - Math.floor(h / 60);
    var p = v*(1-s), q = v*(1-f*s), t = v*(1-(1-f)*s);
    if(i===0){r=v;g=t;b=p;}else if(i===1){r=q;g=v;b=p;}else if(i===2){r=p;g=v;b=t;}
    else if(i===3){r=p;g=q;b=v;}else if(i===4){r=t;g=p;b=v;}else{r=v;g=p;b=q;}
    return '#'+[r,g,b].map(function(x){return Math.round(x*255).toString(16).padStart(2,'0');}).join('').toUpperCase();
  }
  function hexToHSV(hex) {
    hex = hex.replace('#',''); if(hex.length!==6) return null;
    var r=parseInt(hex.slice(0,2),16)/255, g=parseInt(hex.slice(2,4),16)/255, b=parseInt(hex.slice(4,6),16)/255;
    var max=Math.max(r,g,b), min=Math.min(r,g,b), d=max-min, h=0;
    if(d){ if(max===r) h=((g-b)/d+6)%6; else if(max===g) h=(b-r)/d+2; else h=(r-g)/d+4; h*=60; }
    return {h:h, s:max===0?0:d/max, v:max};
  }

  function ensureColorPicker() {
    if (document.getElementById('os9-color-picker-overlay')) return;
    var pH=0, pS=0, pV=1, pTarget=null, pOrigHex='#FFFFFF', pDragging=null;
    var ov = document.createElement('div');
    ov.id = 'os9-color-picker-overlay';
    ov.style.cssText = 'display:none;position:fixed;inset:0;background:rgba(0,0,0,0.3);z-index:99999;align-items:center;justify-content:center;';
    ov.innerHTML = [
      '<div id="os9-cp-win" style="background:var(--os9-gray-300);border-top:1px solid var(--os9-white);border-left:1px solid var(--os9-white);border-right:1px solid var(--os9-gray-800);border-bottom:1px solid var(--os9-gray-800);outline:1px solid var(--os9-black);box-shadow:2px 4px 10px rgba(0,0,0,0.45);width:390px;user-select:none;font-family:var(--os9-font-body);font-size:var(--os9-size-ui);">',
        '<div id="os9-cp-titlebar" style="height:22px;background:var(--os9-gray-300);border-bottom:1px solid var(--os9-gray-600);box-shadow:0 1px 0 var(--os9-white);display:flex;align-items:center;position:relative;overflow:hidden;cursor:move;">',
          '<div id="os9-cp-close" style="width:13px;height:13px;margin:0 6px;flex-shrink:0;z-index:1;cursor:pointer;background:var(--os9-gray-300);border-top:1px solid var(--os9-white);border-left:1px solid var(--os9-white);border-right:1px solid var(--os9-gray-800);border-bottom:1px solid var(--os9-gray-800);"></div>',
          '<div style="position:absolute;left:0;right:0;top:2px;bottom:2px;background:repeating-linear-gradient(to right,transparent 0,transparent 1px,var(--os9-gray-500) 1px,var(--os9-gray-500) 2px,transparent 2px,transparent 3px,var(--os9-gray-200) 3px,var(--os9-gray-200) 4px);"></div>',
          '<div style="position:absolute;left:50%;transform:translateX(-50%);font-family: var(--os9-font-ui);font-size:var(--os9-size-title);font-weight:bold;color:var(--os9-black);background:var(--os9-gray-300);padding:0 8px;z-index:1;white-space:nowrap;">Color Picker</div>',
        '</div>',
        '<div style="padding:10px 12px 12px;display:flex;flex-direction:column;gap:10px;">',
          '<div style="display:flex;gap:10px;align-items:flex-start;">',
            '<div id="os9-cp-sbwrap" style="position:relative;width:200px;height:180px;flex-shrink:0;border-top:1px solid var(--os9-gray-800);border-left:1px solid var(--os9-gray-800);border-right:1px solid var(--os9-white);border-bottom:1px solid var(--os9-white);outline:1px solid var(--os9-gray-600);cursor:crosshair;">',
              '<canvas id="os9-cp-sb" width="200" height="180" style="width:100%;height:100%;display:block;touch-action:none;"></canvas>',
              '<div id="os9-cp-sbcursor" style="position:absolute;width:10px;height:10px;border-radius:50%;border:2px solid var(--os9-white);box-shadow:0 0 0 1px var(--os9-black);transform:translate(-50%,-50%);pointer-events:none;top:0;left:100%;"></div>',
            '</div>',
            '<div id="os9-cp-huewrap" style="position:relative;width:20px;height:180px;flex-shrink:0;border-top:1px solid var(--os9-gray-800);border-left:1px solid var(--os9-gray-800);border-right:1px solid var(--os9-white);border-bottom:1px solid var(--os9-white);outline:1px solid var(--os9-gray-600);cursor:ns-resize;touch-action:none;">',
              '<canvas id="os9-cp-hue" width="20" height="180" style="width:100%;height:100%;display:block;"></canvas>',
              '<img id="os9-cp-huethumb" src="" style="position:absolute;left:50%;transform:translateX(-50%) translateY(-50%) rotate(90deg);width:20px;height:16px;pointer-events:none;image-rendering:pixelated;top:0;" alt="">',
            '</div>',
            '<div style="display:flex;flex-direction:column;gap:8px;flex:1;min-width:0;">',
              '<div style="border-top:1px solid var(--os9-gray-800);border-left:1px solid var(--os9-gray-800);border-right:1px solid var(--os9-white);border-bottom:1px solid var(--os9-white);outline:1px solid var(--os9-gray-600);overflow:hidden;height:64px;">',
                '<div id="os9-cp-new" style="width:100%;height:50%;display:flex;align-items:center;justify-content:flex-end;padding-right:4px;"><span style="font-size:var(--os9-size-body);color:rgba(255,255,255,0.85);">new</span></div>',
                '<div id="os9-cp-current" style="width:100%;height:50%;display:flex;align-items:center;justify-content:flex-end;padding-right:4px;"><span style="font-size:var(--os9-size-body);color:rgba(255,255,255,0.85);">current</span></div>',
              '</div>',
              '<div style="display:flex;align-items:center;gap:4px;"><span style="font-family: var(--os9-font-mono);font-size:var(--os9-size-title);">#</span><input id="os9-cp-hex" maxlength="6" spellcheck="false" style="font-family: var(--os9-font-mono);font-size:var(--os9-size-ui);width:60px;height:18px;border-top:1px solid var(--os9-gray-800);border-left:1px solid var(--os9-gray-800);border-right:1px solid var(--os9-gray-300);border-bottom:1px solid var(--os9-gray-300);background:var(--os9-white);padding:0 3px;text-transform:uppercase;"></div>',
              '<div style="flex:1;min-height:4px;"></div>',
              '<div style="display:flex;flex-direction:column;gap:5px;">',
                '<button id="os9-cp-ok" style="font-family: var(--os9-font-ui);font-size:var(--os9-size-ui);height:20px;background:var(--os9-gray-300);border-top:1px solid var(--os9-white);border-left:1px solid var(--os9-white);border-right:1px solid var(--os9-gray-800);border-bottom:1px solid var(--os9-gray-800);outline:2px solid var(--os9-black);cursor:pointer;color:var(--os9-black);font-weight:bold;width:100%;">OK</button>',
                '<button id="os9-cp-cancel" style="font-family: var(--os9-font-ui);font-size:var(--os9-size-ui);height:20px;background:var(--os9-gray-300);border-top:1px solid var(--os9-white);border-left:1px solid var(--os9-white);border-right:1px solid var(--os9-gray-800);border-bottom:1px solid var(--os9-gray-800);outline:1px solid var(--os9-gray-600);cursor:pointer;color:var(--os9-black);width:100%;">Cancel</button>',
              '</div>',
            '</div>',
          '</div>',
        '</div>',
      '</div>',
    ].join('');
    document.body.appendChild(ov);
    document.getElementById('os9-cp-huethumb').src = PILL_URL;

    var sb=document.getElementById('os9-cp-sb'), sbCtx=sb&&sb.getContext('2d');
    var hc=document.getElementById('os9-cp-hue'), hCtx=hc&&hc.getContext('2d');
    var sbCur=document.getElementById('os9-cp-sbcursor'), hThumb=document.getElementById('os9-cp-huethumb');
    var prevNew=document.getElementById('os9-cp-new'), prevCur=document.getElementById('os9-cp-current');
    var hexIn=document.getElementById('os9-cp-hex');

    function drawSB(){if(!sbCtx)return;var w=sb.width,h=sb.height;var gH=sbCtx.createLinearGradient(0,0,w,0);gH.addColorStop(0,'#fff');gH.addColorStop(1,hsvToHex(pH,1,1));sbCtx.fillStyle=gH;sbCtx.fillRect(0,0,w,h);var gV=sbCtx.createLinearGradient(0,0,0,h);gV.addColorStop(0,'rgba(0,0,0,0)');gV.addColorStop(1,'#000');sbCtx.fillStyle=gV;sbCtx.fillRect(0,0,w,h);sbCur.style.left=(pS*w)+'px';sbCur.style.top=((1-pV)*h)+'px';}
    function drawHue(){if(!hCtx)return;var h=hc.height;var g=hCtx.createLinearGradient(0,0,0,h);[0,60,120,180,240,300,360].forEach(function(d,i,a){g.addColorStop(i/(a.length-1),'hsl('+d+',100%,50%)');});hCtx.fillStyle=g;hCtx.fillRect(0,0,hc.width,h);hThumb.style.top=(pH/360*h)+'px';}
    function updatePreview(){var hex=hsvToHex(pH,pS,pV);prevNew.style.background=hex;hexIn.value=hex.replace('#','').toUpperCase();}
    function openPicker(swatchEl){pTarget=swatchEl;pOrigHex=swatchEl.dataset.colorHex||'#FFFFFF';if(prevCur)prevCur.style.background=pOrigHex;var hsv=hexToHSV(pOrigHex.replace('#',''));if(hsv){pH=hsv.h;pS=hsv.s;pV=hsv.v;}drawSB();drawHue();updatePreview();ov.style.display='flex';}
    window._os9OpenColorPicker = openPicker;
    function closePicker(apply){if(apply&&pTarget){var hex=hsvToHex(pH,pS,pV);pTarget.style.background=hex;pTarget.dataset.colorHex=hex;pTarget.dataset.colorValue=hex;var inner=pTarget.querySelector('span');if(inner)inner.textContent=hex.toUpperCase();var lbl=pTarget.nextElementSibling;if(lbl&&lbl.tagName==='INPUT')lbl.value=hex;}ov.style.display='none';pTarget=null;}
    function handleSB(e){var r=sb.getBoundingClientRect();pS=Math.max(0,Math.min(1,(e.clientX-r.left)/r.width));pV=1-Math.max(0,Math.min(1,(e.clientY-r.top)/r.height));drawSB();updatePreview();}
    function handleHue(e){var r=hc.getBoundingClientRect();pH=Math.max(0,Math.min(359.9,((e.clientY-r.top)/r.height)*360));drawSB();drawHue();updatePreview();}

    document.getElementById('os9-cp-sbwrap').addEventListener('mousedown',function(e){pDragging='sb';handleSB(e);});
    document.getElementById('os9-cp-huewrap').addEventListener('mousedown',function(e){pDragging='hue';handleHue(e);});
    document.addEventListener('mousemove',function(e){if(pDragging==='sb')handleSB(e);else if(pDragging==='hue')handleHue(e);});
    document.addEventListener('mouseup',function(){pDragging=null;});
    sb.addEventListener('touchstart',function(e){e.preventDefault();pDragging='sb';handleSB(e.touches[0]);},{passive:false});
    sb.addEventListener('touchmove',function(e){e.preventDefault();if(pDragging==='sb')handleSB(e.touches[0]);},{passive:false});
    hc.parentElement.addEventListener('touchstart',function(e){e.preventDefault();pDragging='hue';handleHue(e.touches[0]);},{passive:false});
    hc.parentElement.addEventListener('touchmove',function(e){e.preventDefault();if(pDragging==='hue')handleHue(e.touches[0]);},{passive:false});
    document.addEventListener('touchend',function(){pDragging=null;});
    hexIn.addEventListener('input',function(){var v=this.value.replace(/[^a-fA-F0-9]/g,'');if(v.length===6){var hsv=hexToHSV(v);if(hsv){pH=hsv.h;pS=hsv.s;pV=hsv.v;drawSB();drawHue();updatePreview();}}});
    hexIn.addEventListener('keydown',function(e){if(e.key==='Enter')closePicker(true);});
    document.getElementById('os9-cp-ok').addEventListener('click',function(){closePicker(true);});
    document.getElementById('os9-cp-cancel').addEventListener('click',function(){closePicker(false);});
    document.getElementById('os9-cp-close').addEventListener('click',function(){closePicker(false);});
    ov.addEventListener('mousedown',function(e){if(e.target===ov)closePicker(false);});
    (function(){var win=document.getElementById('os9-cp-win'),tb=document.getElementById('os9-cp-titlebar'),dx=0,dy=0,drag=false;tb.addEventListener('mousedown',function(e){if(e.target.id==='os9-cp-close')return;var r=win.getBoundingClientRect();win.style.cssText='background:var(--os9-gray-300);border-top:1px solid var(--os9-white);border-left:1px solid var(--os9-white);border-right:1px solid var(--os9-gray-800);border-bottom:1px solid var(--os9-gray-800);outline:1px solid var(--os9-black);box-shadow:2px 4px 10px rgba(0,0,0,0.45);width:390px;user-select:none;font-family:var(--os9-font-body);font-size:var(--os9-size-ui);position:fixed;left:'+r.left+'px;top:'+r.top+'px;width:'+r.width+'px;height:'+r.height+'px;';dx=e.clientX-r.left;dy=e.clientY-r.top;drag=true;e.preventDefault();});document.addEventListener('mousemove',function(e){if(!drag)return;win.style.left=(e.clientX-dx)+'px';win.style.top=(e.clientY-dy)+'px';});document.addEventListener('mouseup',function(){drag=false;});})();
  }

  function buildColorSlot(index, containerEl, addBtn) {
    var slot = document.createElement('div');
    slot.style.cssText = 'display:flex;align-items:center;gap:6px;';
    slot.dataset.colorSlot = '1';

    /* ── Large color preview chip — clicking opens the picker ── */
    var preview = document.createElement('div');
    preview.style.cssText = 'display:block;width:22px;height:22px;min-width:22px;min-height:22px;flex-shrink:0;box-sizing:border-box;cursor:pointer;border-top:1px solid var(--os9-white);border-left:1px solid var(--os9-white);border-right:1px solid var(--os9-gray-800);border-bottom:1px solid var(--os9-gray-800);outline:1px solid var(--os9-black);background:var(--os9-white);';
    preview.title = 'Click to choose a color';
    preview.dataset.colorHex = preview.dataset.colorValue = '#FFFFFF';

    preview.addEventListener('click', function () {
      if (typeof window._os9OpenColorPicker === 'function') window._os9OpenColorPicker(preview);
    });

    var hexInput = document.createElement('input');
    hexInput.type = 'text'; hexInput.value = '#FFFFFF';
    hexInput.style.cssText = 'width:70px;height:20px;font-family: var(--os9-font-mono);font-size:var(--os9-size-ui);border-top:1px solid var(--os9-gray-800);border-left:1px solid var(--os9-gray-800);border-right:1px solid var(--os9-gray-300);border-bottom:1px solid var(--os9-gray-300);padding:0 4px;background:var(--os9-white);';
    hexInput.addEventListener('input', function () {
      var raw = hexInput.value.replace(/^#/, '');
      if (/^[0-9a-fA-F]{6}$/.test(raw)) {
        var hex = '#' + raw.toUpperCase();
        preview.style.background = preview.dataset.colorValue = preview.dataset.colorHex = hex;
      }
    });

    var removeBtn = document.createElement('button');
    removeBtn.textContent = '×';
    removeBtn.style.cssText = 'width:18px;height:18px;font-size:var(--os9-size-title);background:var(--os9-gray-300);border-top:1px solid var(--os9-white);border-left:1px solid var(--os9-white);border-right:1px solid var(--os9-gray-800);border-bottom:1px solid var(--os9-gray-800);cursor:default;flex-shrink:0;display:' + (index === 0 ? 'none' : 'block') + ';';
    removeBtn.addEventListener('click', function () {
      slot.remove();
      if (addBtn) addBtn.style.display = containerEl.querySelectorAll('[data-color-slot]').length >= 12 ? 'none' : 'block';
    });

    slot.appendChild(preview); slot.appendChild(hexInput); slot.appendChild(removeBtn);
    return slot;
  }

  function initColorSlots(body, slotContainerId, addBtnId) {
    var container = body.querySelector('#' + slotContainerId);
    var addBtn    = body.querySelector('#' + addBtnId);
    if (!container) return;
    container.appendChild(buildColorSlot(0, container, addBtn));
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var count = container.querySelectorAll('[data-color-slot]').length;
        if (count < 12) {
          container.appendChild(buildColorSlot(count, container, addBtn));
          addBtn.style.display = count + 1 >= 12 ? 'none' : 'block';
        }
      });
    }
  }

  /* ────────────────────────────────────────────────────────────
     COLLECTION / PRODUCT BROWSER
  ──────────────────────────────────────────────────────────── */

  function renderList(products) {
    if (!products.length) return '<div class="os9-win__loading"><div>No products found.</div></div>';
    const rows = products.map(p => {
      const price = p.variants && p.variants[0] ? formatPrice(p.variants[0].price) : '';
      let imgSrc = null;
      if (p.featured_image) imgSrc = typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image.src || null);
      else if (p.images && p.images[0]) imgSrc = typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src;
      /* Was the legacy `_small` filename suffix, which only works on the older
         CDN path shape and silently no-ops on the rest. `width` is honoured
         everywhere and lets us ask for the exact size we paint. */
      const imgUrl = imgSrc ? thumbUrl(imgSrc, 64) : null;
      const img = imgUrl
        ? `<img class="os9-list__thumb" src="${imgUrl}" alt="" width="32" height="32" loading="lazy">`
        : `<div class="os9-list__thumb"></div>`;
      return `<div class="os9-list__row" data-product-handle="${p.handle}" role="button" tabindex="0">
        ${img}
        <span class="os9-list__name">${p.title}</span>
        <span class="os9-list__price">${price}</span>
      </div>`;
    }).join('');
    return `<div class="os9-list__header">
      <span class="os9-list__header-name">Name</span>
      <span class="os9-list__header-price">Price</span>
    </div><div class="os9-list__rows">${rows}</div>`;
  }

  function renderProductDetail(product) {
    const variant  = product.variants && product.variants[0];
    const price    = variant ? formatPrice(variant.price) : '';
    let fullImgSrc = null;
    if (product.featured_image) fullImgSrc = typeof product.featured_image === 'string' ? product.featured_image : (product.featured_image.src || null);
    else if (product.images && product.images[0]) fullImgSrc = typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src;
    /* The detail view paints at most 220px tall, so it loads a 440px copy;
       data-fullimg keeps the original for the click-to-zoom overlay, which is
       the one place the full-resolution file is actually worth its weight. */
    const img = fullImgSrc
      ? `<img src="${thumbUrl(fullImgSrc, 440)}" alt="${product.title}" data-fullimg="${fullImgSrc}"
           style="max-width:100%;max-height:220px;object-fit:contain;display:block;margin:0 auto;cursor:zoom-in;"
           onclick="(function(el){var o=document.createElement('div');o.style.cssText='position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:99999;display:flex;align-items:center;justify-content:center;cursor:zoom-out;';var i=document.createElement('img');i.src=el.dataset.fullimg;i.style.cssText='max-width:90vw;max-height:90vh;object-fit:contain;border:2px solid var(--os9-gray-600);box-shadow:4px 4px 0 rgba(0,0,0,0.5);';o.appendChild(i);o.onclick=function(){o.remove();};document.body.appendChild(o);})(this)">`
      : `<div style="width:100%;height:160px;background:var(--os9-gray-200);border:1px solid var(--os9-gray-400);"></div>`;
    const sizeSelector = product.variants && product.variants.length > 1
      ? `<div style="display:flex;gap:8px;padding:5px 0;border-bottom:1px solid var(--os9-gray-500);align-items:center;">
          <span style="font-weight:bold;width:48px;flex-shrink:0;">Size:</span>
          <select data-variant-select style="flex:1;height:22px;border:1px solid var(--os9-gray-600);padding:0 4px;font-size:var(--os9-size-ui);font-family: var(--os9-font-body);background:var(--os9-white);"></select>
        </div>`
      : `<input type="hidden" data-variant-select value="${variant ? variant.id : ''}">`;
    const descText  = product.body_html ? stripTags(product.body_html).trim() : '';
    const descBlock = descText
      ? `<div style="padding:6px 0;border-bottom:1px solid var(--os9-gray-500);font-family: var(--os9-font-body);font-size:var(--os9-size-ui);color:var(--os9-black);line-height:1.5;">${descText}</div>`
      : '';
    return `<div style="background:var(--os9-gray-300);padding:12px;display:flex;flex-direction:column;gap:8px;min-height:100%;box-sizing:border-box;font-family: var(--os9-font-body);font-size:var(--os9-size-ui);overflow-y:visible;">
      <div style="border:2px inset var(--os9-gray-500);background:var(--os9-white);padding:8px;text-align:center;">${img}</div>
      <div>
        <div style="display:flex;gap:8px;padding:5px 0;border-bottom:1px solid var(--os9-gray-500);">
          <span style="font-weight:bold;width:48px;flex-shrink:0;">Title:</span>
          <span style="font-family: var(--os9-font-ui);font-size:13px;font-weight:bold;">${product.title}</span>
        </div>
        <div style="display:flex;gap:8px;padding:5px 0;border-bottom:1px solid var(--os9-gray-500);">
          <span style="font-weight:bold;width:48px;flex-shrink:0;">Price:</span>
          <span>${price}</span>
        </div>
        ${descBlock}
        ${sizeSelector}
      </div>
      <div style="display:flex;justify-content:flex-end;margin-top:2px;">
        <button data-detail-atc style="height:26px;min-width:120px;background:linear-gradient(to bottom,var(--os9-azul-mid),var(--os9-azul-mid));border:1px solid var(--os9-azul-dark);border-radius:4px;font-family: var(--os9-font-ui);font-size:var(--os9-size-ui);font-weight:bold;color:var(--os9-white);cursor:default;padding:0 14px;">Add to Cart</button>
      </div>
    </div>`;
  }

  function wireWindowContent(el, winState) {
    const body      = el.querySelector('[data-body]');
    const infobar   = el.querySelector('[data-infobar]');
    const backbar   = el.querySelector('[data-backbar]');
    const backBtn   = el.querySelector('[data-back]');
    const backTitle = el.querySelector('[data-backbar-title]');
    const titleEl   = el.querySelector('.os9-win__title');
    const statusbar = el.querySelector('[data-statusbar]');
    if (body.dataset.wired) return;
    body.dataset.wired = '1';

    /* ── In-window navigation stack ────────────────────────────────────
       Finder-style drill-down: Cart → Collection → Product all render into
       this one window, and Back pops one level. This replaces the previous
       single prevProducts/prevTitle pair, which could only remember one step
       and so could never support a two-level path from the cart.

       A frame is { kind, title, infobar, render } — render() repaints the
       body. Frames close over data already fetched, so Back never refetches.
       The backbar is part of every window template (buildWindow), it was
       simply unused outside collection windows. */
    winState.stack = winState.stack || [];

    function applyFrame(frame) {
      winState.current = frame;
      if (titleEl) titleEl.textContent = frame.title || '';
      if (infobar) infobar.textContent = frame.infobar || '';
      const depth = winState.stack.length;
      if (backbar) {
        if (depth) {
          backbar.style.display = 'flex'; backbar.dataset.visible = '1';
          if (backTitle) backTitle.textContent = winState.stack[depth - 1].title || '';
        } else {
          backbar.style.display = 'none'; delete backbar.dataset.visible;
        }
      }
      syncInfobarState(el);
      frame.render();

      /* The product view is the one frame that earns a resize. Its Add to
         Cart button sits at the bottom of the content, and in a 380x400
         utility window that lands below the fold — so the primary action on
         the page is something you have to go looking for. Grow the window to
         fit it, remember the size we grew from, and put that back when Back
         leaves the product.

         Every other frame still keeps whatever size it was opened or dragged
         to, and taller content scrolls. That part of the original rule
         stands; this is a deliberate exception for the one view where the
         thing below the fold is the thing you came to press. */
      if (frame.kind === 'product') {
        fitToContent();
        /* The product image loads after this paint, so the first measurement
           is short by its height — re-fit once it arrives.

           The re-fit MUST check that we are still on the frame that asked for
           it. Navigating Back before the image finished would otherwise fire
           this against whatever frame is showing now: it would grow the
           Finder to the product's height and record the Finder's size as the
           one to restore, which is what left the window mis-sized after a
           few rounds of clicking in and back out. */
        var detailImg = body.querySelector('img[data-fullimg]');
        if (detailImg && !detailImg.complete) {
          var refit = function () { if (winState.current === frame) fitToContent(); };
          detailImg.addEventListener('load',  refit, { once: true });
          detailImg.addEventListener('error', refit, { once: true });
        }
      } else {
        restoreGeometry();
      }
    }

    function fitToContent() {
      /* These states are tracked as dataset flags, not classes — `is-zoomed`
         and `is-minimized` exist only as CSS selectors, so testing for them
         here was a guard that never fired. */
      if (el.dataset.zoomed || el.dataset.fullscreenZoom ||
          el.dataset.kbZoomed || el.dataset.minimized) return;
      var b = getDesktopBounds();
      if (!b.width || !b.height) return;

      /* Everything that is not the scrolling body: titlebar, backbar,
         infobar, statusbar. Measured rather than assumed, because which of
         those are present varies by frame. */
      var chromeH = el.offsetHeight - body.clientHeight;
      var needed  = chromeH + body.scrollHeight;
      var maxH    = Math.max(160, b.height - 8);
      var newH    = Math.min(needed, maxH);
      /* Compare before padding the result, or `needed` always lands a couple
         of pixels above the current height even when the content fits exactly
         and every drill-in grows the window by that much and then restores
         it — a visible twitch on each navigation. */
      if (newH <= el.offsetHeight + 2) return;   /* already tall enough */
      newH += 2;

      if (!winState.savedGeom) {
        winState.savedGeom = { height: el.style.height, top: el.style.top };
      }
      el.style.height = newH + 'px';
      /* A grown product view wants to STAY grown across a browser resize, so
         the grown height is the desired height until restoreGeometry undoes it. */
      el.dataset.desiredH = newH;
      winState.grewTo = newH;

      /* Growing downward can push the window past the bottom of the desktop;
         lift it just enough to keep the whole thing on screen. */
      var top = parseFloat(el.style.top) || 0;
      if (top + newH > b.height) {
        el.style.top = Math.max(b.top, b.height - newH - 4) + 'px';
      }
    }

    function restoreGeometry() {
      if (!winState.savedGeom) return;
      /* If the height no longer matches what we grew to, the user has resized
         the window themselves since — their size wins, so leave it alone. */
      if (winState.grewTo == null || Math.abs(el.offsetHeight - winState.grewTo) < 2) {
        el.style.height = winState.savedGeom.height;
        el.style.top    = winState.savedGeom.top;
        el.dataset.desiredH = el.offsetHeight;
      }
      winState.savedGeom = null;
      winState.grewTo    = null;
    }
    function navTo(frame) {
      if (winState.current) winState.stack.push(winState.current);
      applyFrame(frame);
    }
    function navBack() {
      if (!winState.stack.length) return;
      applyFrame(winState.stack.pop());
    }
    /* Used when the Cart icon is clicked while this window is already deep in
       a collection — return to the root rather than opening a duplicate. */
    function navReset() {
      const root = winState.stack.length ? winState.stack[0] : winState.current;
      winState.stack.length = 0;
      if (root) applyFrame(root);
    }

    function collectionFrame(products, title) {
      const n = products.length;
      return {
        kind: 'collection', title: title,
        infobar: n + ' item' + (n !== 1 ? 's' : ''),
        render: function () { body.innerHTML = renderList(products); },
      };
    }
    function productFrame(product) {
      return {
        kind: 'product', title: product.title, infobar: '',
        render: function () {
          body.innerHTML = renderProductDetail(product);
          /* Click-to-zoom pulls the original, which is the heaviest single
             request in the shop. Warm it now, while the visitor is reading
             the description. */
          var zoomSrc = (product.images && product.images[0] &&
                        (product.images[0].src || product.images[0])) || null;
          if (zoomSrc) warmImages([zoomSrc]);
          const sel = body.querySelector('[data-variant-select]');
          if (sel && sel.tagName === 'SELECT' && product.variants) {
            const ph = document.createElement('option');
            ph.value = ''; ph.textContent = '— Select Size —'; ph.selected = true;
            sel.appendChild(ph);
            product.variants.forEach(v => {
              const opt = document.createElement('option');
              opt.value = v.id; opt.textContent = v.title + (v.available ? '' : ' — Sold Out');
              opt.dataset.available = v.available ? '1' : '0';
              if (!v.available) opt.disabled = true;
              sel.appendChild(opt);
            });
            if (window.OS9Select) window.OS9Select(sel);
          }
        },
      };
    }

    /* Opening a collection from inside the cart: push, don't spawn a window. */
    async function openCollectionInPlace(handle, title) {
      body.innerHTML = loadingHTML('Loading collection...');
      try {
        const products = await fetchCollection(handle);
        winState.products = products;
        navTo(collectionFrame(products, title));
      } catch {
        body.innerHTML = `<div class="os9-win__loading"><div>Could not load collection.</div></div>`;
      }
    }

    /* Jump straight to one product — used by shop links in the Geocities
       gallery, so buying a piece lands in the Finder like everything else. */
    async function openProductInPlace(handle) {
      body.innerHTML = loadingHTML('Loading product...');
      try { navTo(productFrame(await fetchProduct(handle))); }
      catch { body.innerHTML = `<div class="os9-win__loading"><div>Error loading product.</div></div>`; }
    }

    winState.nav = {
      to: navTo, back: navBack, reset: navReset, apply: applyFrame,
      collectionFrame, productFrame, openCollectionInPlace, openProductInPlace,
      isShowing: function (kind) { return !!winState.current && winState.current.kind === kind; },
    };

    body.addEventListener('click', async e => {
      const atcBtn = e.target.closest('[data-detail-atc]');
      if (atcBtn) {
        const sel = body.querySelector('[data-variant-select]');
        const variantId = sel ? sel.value : '';
        if (!variantId) { showDialog({ title: 'Error', message: 'Please select a size.', icon: '&#9888;', buttons: 'OK' }); return; }
        const opt = sel.options ? sel.options[sel.selectedIndex] : null;
        if (opt && opt.dataset.available === '0') { showDialog({ title: 'Error', message: 'This size is sold out.', icon: '&#9888;', buttons: 'OK' }); return; }
        atcBtn.textContent = 'Adding...'; atcBtn.disabled = true;
        try {
          await addToCart(variantId);
          atcBtn.textContent = 'Added ✓';
          setTimeout(() => { atcBtn.textContent = 'Add to Cart'; atcBtn.disabled = false; }, 1500);
          const cart = await fetchCart();
          updateTrashCount(cart.item_count);
          updateCartBadge(cart.item_count);
          const cartEl = document.getElementById('os9-cart-win');
          if (cartEl && cartEl._refreshCart) cartEl._refreshCart();

          /* Adding is the end of the errand, so go all the way out to Squiche
             HD rather than back one level to the folder. The root is where My
             Items lives, so the shopper lands looking at the cart they just
             added to — and it is the same place they would reach by closing
             the product themselves, only without the clicks.

             The pause lets "Added ✓" land first; navigating instantly reads as
             the click having failed.

             Guarded on still being on the same product: if they navigated
             somewhere else while the request was in flight, this would yank
             them out of wherever they now are. Requiring a non-empty stack
             keeps a directly-opened product window where it is, since that
             product IS the root and navReset() would just re-apply it. */
          const addedFrom = winState.current;
          setTimeout(() => {
            if (winState.current === addedFrom && winState.stack.length) navReset();
          }, 900);
        } catch {
          atcBtn.textContent = 'Add to Cart'; atcBtn.disabled = false;
          showDialog({ title: 'Error', message: 'Could not add to cart.', icon: '&#9888;' });
        }
        return;
      }
      const row = e.target.closest('[data-product-handle]');
      if (!row) return;
      const handle = row.dataset.productHandle;
      body.querySelectorAll('.os9-list__row').forEach(r => r.classList.remove('is-selected'));
      row.classList.add('is-selected');
      body.innerHTML = loadingHTML('Loading product...');
      try {
        navTo(productFrame(await fetchProduct(handle)));
      } catch { body.innerHTML = `<div class="os9-win__loading"><div>Error loading product.</div></div>`; }
    });

    if (backBtn) backBtn.addEventListener('click', navBack);

    /* The hover readout that used to write a product name into a strip along
       the window's bottom edge is gone with that strip. In OS 9 the window
       frame IS the two scroll bars meeting at the size box, and an 18px bar
       below them broke that frame and pushed the grow box off it. */
  }

  /* Every shop entry point — the browser's bookmark links, the Finder, and the
     desktop icons — now lands in the single shared shop window, so the window
     size, the scrollbar, and the Back stack behave identically no matter where
     the user came in. Previously this built a separate window per collection,
     with its own height and a Back that could only reach the collection list,
     never the cart. */
  async function openCollection({ handle, title, x, y }) {
    const el = await openCart({ x: x, y: y, root: 'finder' });
    const st = windows.get('os9-cart-win');
    const nav = st && st.winState && st.winState.nav;
    if (!nav) return el;
    /* openCart() has just reset to the cart root, so pushing the collection
       here leaves Cart underneath it — Back walks Product → Collection → Cart. */
    nav.openCollectionInPlace(handle, title);
    return el;
  }

  /* ────────────────────────────────────────────────────────────
     CONTACT FORM
  ──────────────────────────────────────────────────────────── */

  function contactFormHTML() {
    return '<div style="background:var(--os9-gray-300);min-height:100%;display:flex;flex-direction:column;font-family:var(--os9-font-body);font-size:var(--os9-size-title);">' +
      '<div style="background:var(--os9-azul-mid);color:var(--os9-white);padding:4px 10px;font-family:var(--os9-font-ui);font-size:var(--os9-size-ui);font-weight:bold;border-bottom:1px solid var(--os9-azul-mid);">Email — Squiche Design Lab</div>' +
      '<div style="padding:12px 12px 12px 20px;flex:1;overflow-y:auto;">' +
        '<p style="margin:0 0 10px;line-height:1.5;color:var(--os9-black);font-size:var(--os9-size-ui);">Thanks for thinking of Squiche&#8482;! Fill out the form below and we will be in touch shortly.</p>' +
        '<table style="width:100%;border-collapse:collapse;margin-bottom:10px;table-layout:fixed;">' +
          '<tr><td style="width:70px;padding:3px 8px;font-weight:bold;font-size:var(--os9-size-ui);vertical-align:middle;">From:</td><td style="padding:2px 0;"><input id="os9-contact-name" type="text" placeholder="Your name" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></td></tr>' +
          '<tr><td style="padding:3px 8px;font-weight:bold;font-size:var(--os9-size-ui);vertical-align:middle;">Email:</td><td style="padding:2px 0;"><input id="os9-contact-email" type="email" placeholder="you@example.com" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></td></tr>' +
          '<tr><td style="padding:3px 8px;font-weight:bold;font-size:var(--os9-size-ui);vertical-align:middle;">Phone:</td><td style="padding:2px 0;"><input id="os9-contact-phone" type="tel" placeholder="215-555-5555" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></td></tr>' +
          '<tr><td style="padding:3px 8px;font-weight:bold;font-size:var(--os9-size-ui);vertical-align:middle;">Subject:</td><td style="padding:2px 0;"><input id="os9-contact-subject" type="text" value="Graphic Design Inquiry" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></td></tr>' +
          '<tr><td style="padding:3px 8px;font-weight:bold;font-size:var(--os9-size-ui);vertical-align:middle;">Service:</td><td style="padding:2px 0;"><select id="os9-contact-project" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 4px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);"><option value="" disabled selected>Select one...</option><option value="general">General Inquiry</option><option value="event">Event Flier</option><option value="merch">Merch Design</option><option value="brand">Brand Consulting</option><option value="print">Print Design</option><option value="web">Web Design</option></select></td></tr>' +
        '</table>' +
        '<div id="os9-panel-general" class="os9-panel" style="border:1px solid var(--os9-gray-500);background:var(--os9-gray-400);padding:6px 8px;margin-bottom:8px;"><div style="font-weight:bold;font-size:var(--os9-size-ui);margin-bottom:6px;font-family:var(--os9-font-ui);">General Inquiry Details</div><div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;"><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Ideal Deadline</div><input id="os9-contact-deadline" type="date" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 4px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Budget Range</div><input id="os9-contact-budget" type="text" placeholder="e.g. $250&#x2013;$750" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div></div><div style="margin-top:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Reference / Inspiration Links</div><input type="text" id="os9-contact-refs" placeholder="Paste links to references, moodboards, etc." style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div style="margin-top:6px;margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:4px;">Color Preferences</div><div id="os9-color-slots-general" style="display:flex;flex-direction:column;gap:4px;"></div><button type="button" id="os9-add-color-general" style="margin-top:4px;font-size:var(--os9-size-ui);font-family: var(--os9-font-body);background:var(--os9-gray-300);border-top:1px solid var(--os9-white);border-left:1px solid var(--os9-white);border-right:1px solid var(--os9-gray-800);border-bottom:1px solid var(--os9-gray-800);padding:2px 8px;cursor:default;">+ Add Color</button></div></div>' +
        '<div id="os9-panel-event" class="os9-panel" style="display:none;border:1px solid var(--os9-gray-500);background:var(--os9-gray-400);padding:6px 8px;margin-bottom:8px;"><div style="font-weight:bold;font-size:var(--os9-size-ui);margin-bottom:6px;font-family:var(--os9-font-ui);">Event Details</div><div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px;"><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Event Date</div><input type="date" id="os9-ev-date" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 4px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Flier Deadline</div><input type="date" id="os9-ev-deadline" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 4px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Venue Name</div><input type="text" id="os9-ev-venue" placeholder="e.g. The Fillmore" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Address</div><input type="text" id="os9-ev-address" placeholder="Street, City, State" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Door Cost</div><input type="text" id="os9-ev-door" placeholder="e.g. $15 / Free" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Ticket Link</div><input type="text" id="os9-ev-tickets" placeholder="eventbrite.com/..." style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div></div><div style="margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Acts / Performers</div><input type="text" id="os9-ev-acts" placeholder="List acts, DJ names, performers, etc." style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div style="margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:4px;">Color Preferences</div><div id="os9-color-slots-event" style="display:flex;flex-direction:column;gap:4px;"></div><button type="button" id="os9-add-color-event" style="margin-top:4px;font-size:var(--os9-size-ui);font-family: var(--os9-font-body);background:var(--os9-gray-300);border-top:1px solid var(--os9-white);border-left:1px solid var(--os9-white);border-right:1px solid var(--os9-gray-800);border-bottom:1px solid var(--os9-gray-800);padding:2px 8px;cursor:default;">+ Add Color</button></div></div>' +
        '<div id="os9-panel-merch" class="os9-panel" style="display:none;border:1px solid var(--os9-gray-500);background:var(--os9-gray-400);padding:6px 8px;margin-bottom:8px;"><div style="font-weight:bold;font-size:var(--os9-size-ui);margin-bottom:6px;font-family:var(--os9-font-ui);">Merch Design Details</div><div style="margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:4px;">Print Method</div><label style="margin-right:16px;font-size:var(--os9-size-ui);"><input type="radio" name="os9-merch-method" value="DTG" style="margin-right:6px;width:13px;height:13px;">Direct to Garment</label><label style="font-size:var(--os9-size-ui);"><input type="radio" name="os9-merch-method" value="Screen Print" style="margin-right:6px;width:13px;height:13px;">Screen Print</label></div><div id="os9-merch-screen-opts" style="display:none;margin-bottom:6px;padding:6px;background:var(--os9-gray-500);border:1px solid var(--os9-gray-600);"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:4px;">Screen Print Options</div><label style="display:block;font-size:var(--os9-size-ui);margin-bottom:3px;"><input type="checkbox" id="os9-merch-screen-ready" style="margin-right:6px;width:13px;height:13px;">Screen Ready File Included</label><label style="display:block;font-size:var(--os9-size-ui);"><input type="checkbox" id="os9-merch-sim-process" style="margin-right:6px;width:13px;height:13px;">Simulated Process</label></div><div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px;"><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Print Location</div><input type="text" id="os9-merch-location" placeholder="e.g. Front chest, Full back" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Artwork Size</div><input type="text" id="os9-merch-size" placeholder="e.g. 12x12 inches" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div></div><div style="margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:4px;">Color Preferences</div><div id="os9-color-slots-merch" style="display:flex;flex-direction:column;gap:4px;"></div><button type="button" id="os9-add-color-merch" style="margin-top:4px;font-size:var(--os9-size-ui);font-family: var(--os9-font-body);background:var(--os9-gray-300);border-top:1px solid var(--os9-white);border-left:1px solid var(--os9-white);border-right:1px solid var(--os9-gray-800);border-bottom:1px solid var(--os9-gray-800);padding:2px 8px;cursor:default;">+ Add Color</button></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Deadline</div><input type="date" id="os9-merch-deadline" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 4px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div></div>' +
        '<div id="os9-panel-brand" class="os9-panel" style="display:none;border:1px solid var(--os9-gray-500);background:var(--os9-gray-400);padding:6px 8px;margin-bottom:8px;"><div style="font-weight:bold;font-size:var(--os9-size-ui);margin-bottom:6px;font-family:var(--os9-font-ui);">Brand Consulting Details</div><div style="margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:4px;">Services Needed (select all that apply)</div><label style="display:block;font-size:var(--os9-size-ui);margin-bottom:3px;"><input type="checkbox" id="os9-brand-rebrand" style="margin-right:6px;width:13px;height:13px;">Rebranding</label><label style="display:block;font-size:var(--os9-size-ui);margin-bottom:3px;"><input type="checkbox" id="os9-brand-marketing" style="margin-right:6px;width:13px;height:13px;">Marketing Strategy</label><label style="display:block;font-size:var(--os9-size-ui);margin-bottom:3px;"><input type="checkbox" id="os9-brand-ads" style="margin-right:6px;width:13px;height:13px;">Ad Strategy</label><label style="display:block;font-size:var(--os9-size-ui);margin-bottom:3px;"><input type="checkbox" id="os9-brand-other" style="margin-right:6px;width:13px;height:13px;">Other</label></div></div>' +
        '<div id="os9-panel-print" class="os9-panel" style="display:none;border:1px solid var(--os9-gray-500);background:var(--os9-gray-400);padding:6px 8px;margin-bottom:8px;"><div style="font-weight:bold;font-size:var(--os9-size-ui);margin-bottom:6px;font-family:var(--os9-font-ui);">Print Design Details</div><div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px;"><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Print Type</div><input type="text" id="os9-print-type" placeholder="e.g. Poster, Flier, Banner" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Dimensions</div><input type="text" id="os9-print-size" placeholder="e.g. 8.5x11, 24x36" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Deadline</div><input type="date" id="os9-print-deadline" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 4px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Budget</div><input type="text" id="os9-print-budget" placeholder="e.g. $150&#x2013;$300" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div></div><div style="margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">References / Links</div><input type="text" id="os9-print-refs" placeholder="Paste reference links" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div><div style="margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:4px;">Color Preferences</div><div id="os9-color-slots-print" style="display:flex;flex-direction:column;gap:4px;"></div><button type="button" id="os9-add-color-print" style="margin-top:4px;font-size:var(--os9-size-ui);font-family: var(--os9-font-body);background:var(--os9-gray-300);border-top:1px solid var(--os9-white);border-left:1px solid var(--os9-white);border-right:1px solid var(--os9-gray-800);border-bottom:1px solid var(--os9-gray-800);padding:2px 8px;cursor:default;">+ Add Color</button></div></div>' +
        '<div id="os9-panel-web" class="os9-panel" style="display:none;border:1px solid var(--os9-gray-500);background:var(--os9-gray-400);padding:6px 8px;margin-bottom:8px;"><div style="font-weight:bold;font-size:var(--os9-size-ui);margin-bottom:6px;font-family:var(--os9-font-ui);">Web Design Details</div><div style="margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:4px;">Do you currently have a website?</div><label style="margin-right:16px;font-size:var(--os9-size-ui);"><input type="radio" name="os9-web-existing" value="Yes" style="margin-right:6px;width:13px;height:13px;">Yes</label><label style="font-size:var(--os9-size-ui);"><input type="radio" name="os9-web-existing" value="No" style="margin-right:6px;width:13px;height:13px;">No</label></div><div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px;"><div id="os9-web-platform-group" style="display:none;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Ecom Merchant</div><select id="os9-web-platform" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 4px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);"><option>Not sure</option><option>Shopify</option><option>Squarespace</option><option>WordPress</option><option>Wix</option><option>Webflow</option><option>Custom / Other</option></select></div><div><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Budget Range</div><input type="text" id="os9-web-budget" placeholder="e.g. $500&#x2013;$2000" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div></div><div style="margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:4px;">Ecommerce?</div><label style="margin-right:16px;font-size:var(--os9-size-ui);"><input type="radio" name="os9-web-ecomm" value="Yes" style="margin-right:6px;width:13px;height:13px;">Yes</label><label style="font-size:var(--os9-size-ui);"><input type="radio" name="os9-web-ecomm" value="No" style="margin-right:6px;width:13px;height:13px;">No</label></div><div id="os9-web-dropship-group" style="display:none;margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:4px;">Do you dropship?</div><label style="margin-right:16px;font-size:var(--os9-size-ui);"><input type="radio" name="os9-web-drop" value="Yes" style="margin-right:6px;width:13px;height:13px;">Yes</label><label style="margin-right:16px;font-size:var(--os9-size-ui);"><input type="radio" name="os9-web-drop" value="No" style="margin-right:6px;width:13px;height:13px;">No</label><label style="font-size:var(--os9-size-ui);"><input type="radio" name="os9-web-drop" value="N/A" style="margin-right:6px;width:13px;height:13px;">N/A</label></div><div style="margin-bottom:6px;"><div style="font-size:var(--os9-size-body);font-weight:bold;margin-bottom:2px;">Products / Services being sold</div><input type="text" id="os9-web-products" placeholder="Describe what you sell or offer" style="width:100%;height:22px;border:1px solid var(--os9-gray-600);padding:0 6px;font-size:var(--os9-size-ui);font-family:inherit;background:var(--os9-white);box-sizing:border-box;"></div></div>' +
        '<textarea id="os9-contact-message" rows="3" placeholder="Let us know your ideas!" style="width:100%;border:1px solid var(--os9-gray-600);padding:6px;font-size:var(--os9-size-ui);font-family:inherit;resize:none;overflow:hidden;background:var(--os9-white);margin-bottom:8px;box-sizing:border-box;"></textarea>' +
        '<div style="border:1px dashed var(--os9-gray-500);padding:6px 8px;margin-bottom:10px;font-size:var(--os9-size-body);color:var(--os9-gray-800);line-height:1.4;">Earliest selectable deadline is two business days from today.</div>' +
        '<div style="display:flex;justify-content:flex-end;gap:8px;"><button id="os9-contact-cancel" style="height:26px;min-width:72px;background:linear-gradient(to bottom,var(--os9-gray-200),var(--os9-gray-400));border:1px solid var(--os9-gray-600);border-radius:4px;font-family:var(--os9-font-ui);font-size:var(--os9-size-ui);font-weight:bold;cursor:default;padding:0 14px;">Cancel</button><button id="os9-contact-send" style="height:26px;min-width:72px;background:linear-gradient(to bottom,var(--os9-azul-mid),var(--os9-azul-mid));border:1px solid var(--os9-azul-dark);border-radius:4px;font-family:var(--os9-font-ui);font-size:var(--os9-size-ui);font-weight:bold;color:var(--os9-white);cursor:default;padding:0 14px;">Send</button></div>' +
      '</div>' +
    '</div>';
  }

  function openPage({ url, title, x, y }) {
    var typeKey = 'page:' + (url || 'contact');
    if (bringOrOpen(typeKey)) return;
    var b    = getDesktopBounds();
    var dw   = b.width, dh = b.height;
    /* Match the Squiche HD finder window's desktop width (520px, clamped
       to the desktop area). makeFullscreenOnMobile() below overrides
       these values on mobile viewports, so this only affects desktop. */
    var w    = Math.min(520, dw - 20);
    var h    = Math.min(620, dh - 20);
    var posX = x !== undefined ? x : Math.max(10, Math.floor((dw - w) / 2));
    var posY = y !== undefined ? y : Math.max(b.top, (dh - h) / 3 + b.top);
    var id   = uid();
    var el   = buildWindow({ id, title: title || 'Email', width: w, height: h, x: posX, y: posY });
    getLayer().appendChild(el);
    windows.set(id, { el });
    typeKeys.set(typeKey, id);
    initDrag(el); initResize(el); initMinimize(el);
    initClose(el, id, typeKey); initZoom(el);
    el.addEventListener('mousedown',  () => focusWindow(el));
    el.addEventListener('touchstart', () => focusWindow(el), { passive: true });

    /* Auto-fullscreen on mobile — contact form needs the whole viewport
       to be usable. Helper is a no-op when width > 749px. */
    makeFullscreenOnMobile(el);

    var body    = el.querySelector('[data-body]');
    var infobar = el.querySelector('[data-infobar]');
    infobar.textContent = url || '/pages/contact';
    syncInfobarState(el);
    body.style.padding = '0';
    body.innerHTML = contactFormHTML();
    body.querySelectorAll('select').forEach(OS9Select);

    function switchPanel(val) {
      body.querySelectorAll('.os9-panel').forEach(function (p) { p.style.display = 'none'; });
      var panel = body.querySelector('#os9-panel-' + val);
      if (panel) panel.style.display = 'block';
    }
    switchPanel(''); /* hide all panels — user must choose a service first */

    function getMinBookingDate() {
      var d = new Date(), added = 0;
      while (added < 2) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) added++; }
      return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    }
    var minDate = getMinBookingDate();

    /* Replace every <input type="date"> with three <select> elements (Month / Day / Year).
       The native date picker on iOS pops up a full-screen calendar we don't want.
       A hidden <input> keeps the ISO value so the send handler (val()) works unchanged. */
    function initDatePickers(containerEl, minDateStr) {
      var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      var min    = new Date(minDateStr + 'T00:00:00');
      var today  = new Date();
      var maxYear = today.getFullYear() + 3;
      var selStyle = 'height:22px;font-size:var(--os9-size-ui);font-family:inherit;border:1px solid var(--os9-gray-600);background:var(--os9-white);box-sizing:border-box;min-width:0;';

      containerEl.querySelectorAll('input[type="date"]').forEach(function (hidden) {
        var mSel = document.createElement('select');
        var dSel = document.createElement('select');
        var ySel = document.createElement('select');
        var wrap = document.createElement('div');
        wrap.style.cssText = 'display:flex;gap:3px;width:100%;';
        mSel.style.cssText = selStyle + 'flex:3;';
        dSel.style.cssText = selStyle + 'flex:2;';
        ySel.style.cssText = selStyle + 'flex:2.5;';

        for (var y = today.getFullYear(); y <= maxYear; y++) {
          var yo = document.createElement('option'); yo.value = yo.textContent = String(y);
          ySel.appendChild(yo);
        }
        MONTHS.forEach(function (name, i) {
          var mo = document.createElement('option'); mo.value = String(i); mo.textContent = name;
          mSel.appendChild(mo);
        });

        function rebuildDays() {
          var selY  = parseInt(ySel.value);
          var selM  = parseInt(mSel.value);
          var prevD = parseInt(dSel.value) || 0;
          var total = new Date(selY, selM + 1, 0).getDate();
          dSel.innerHTML = '';
          for (var d = 1; d <= total; d++) {
            if (new Date(selY, selM, d) < min) continue;
            var dopt = document.createElement('option');
            dopt.value = dopt.textContent = String(d);
            if (d === prevD) dopt.selected = true;
            dSel.appendChild(dopt);
          }
          if (!dSel.options.length) {
            /* Entire month is before min — advance to next valid month */
            var nm = selM + 1, ny = selY;
            if (nm > 11) { nm = 0; ny++; }
            mSel.value = String(nm); ySel.value = String(ny);
            rebuildDays(); return;
          }
          if (dSel._os9Refresh) dSel._os9Refresh();
          syncHidden();
        }

        function syncHidden() {
          hidden.value = ySel.value + '-' +
            String(parseInt(mSel.value) + 1).padStart(2, '0') + '-' +
            String(dSel.value || '1').padStart(2, '0');
        }

        ySel.value = String(min.getFullYear());
        mSel.value = String(min.getMonth());

        wrap.appendChild(mSel); wrap.appendChild(dSel); wrap.appendChild(ySel);
        hidden.parentNode.insertBefore(wrap, hidden);
        hidden.style.display = 'none';

        /* Apply OS9Select so the date dropdowns match the Service field style */
        if (window.OS9Select) {
          window.OS9Select(mSel);
          window.OS9Select(dSel);
          window.OS9Select(ySel);
        }

        mSel.addEventListener('change', function () { rebuildDays(); if (mSel._os9Refresh) mSel._os9Refresh(); });
        ySel.addEventListener('change', function () { rebuildDays(); if (ySel._os9Refresh) ySel._os9Refresh(); });
        dSel.addEventListener('change', syncHidden);
        rebuildDays();
        /* Sync triggers after initial population */
        if (mSel._os9Refresh) mSel._os9Refresh();
        if (dSel._os9Refresh) dSel._os9Refresh();
        if (ySel._os9Refresh) ySel._os9Refresh();
      });
    }
    initDatePickers(body, minDate);

    var msgTa = body.querySelector('#os9-contact-message');
    if (msgTa) msgTa.addEventListener('input', function () { this.style.height = 'auto'; this.style.height = this.scrollHeight + 'px'; });

    var projectSel = body.querySelector('#os9-contact-project');
    if (projectSel) projectSel.addEventListener('change', function () { switchPanel(this.value); });

    body.querySelectorAll('input[name="os9-merch-method"]').forEach(function (r) {
      r.addEventListener('change', function () {
        var so = body.querySelector('#os9-merch-screen-opts');
        if (so) so.style.display = this.value === 'Screen Print' ? 'block' : 'none';
      });
    });

    body.querySelectorAll('input[name="os9-web-ecomm"]').forEach(function (r) {
      r.addEventListener('change', function () {
        var pg = body.querySelector('#os9-web-platform-group');
        var dg = body.querySelector('#os9-web-dropship-group');
        if (pg) pg.style.display = this.value === 'Yes' ? 'block' : 'none';
        if (dg) dg.style.display = this.value === 'Yes' ? 'block' : 'none';
      });
    });

    ensureColorPicker();
    initColorSlots(body, 'os9-color-slots-general', 'os9-add-color-general');
    initColorSlots(body, 'os9-color-slots-event',   'os9-add-color-event');
    initColorSlots(body, 'os9-color-slots-merch',   'os9-add-color-merch');
    initColorSlots(body, 'os9-color-slots-print',   'os9-add-color-print');

    var cancelBtn = body.querySelector('#os9-contact-cancel');
    if (cancelBtn) cancelBtn.addEventListener('click', function () { el.remove(); windows.delete(id); typeKeys.delete(typeKey); });

    var sendBtn = body.querySelector('#os9-contact-send');
    if (sendBtn) sendBtn.addEventListener('click', async function () {
      var name    = (body.querySelector('#os9-contact-name')?.value    || '').trim();
      var email   = (body.querySelector('#os9-contact-email')?.value   || '').trim();
      var subject = (body.querySelector('#os9-contact-subject')?.value || '').trim();
      var project = (body.querySelector('#os9-contact-project')?.value || 'general');
      var message = (body.querySelector('#os9-contact-message')?.value || '').trim();
      var phone   = (body.querySelector('#os9-contact-phone')?.value   || '').trim();
      if (!name || !email || !message) {
        showDialog({ title: 'Missing Info', message: 'Please fill in your name, email, and message.', icon: '&#9888;', buttons: 'OK' });
        return;
      }
      sendBtn.textContent = 'Sending...'; sendBtn.style.opacity = '0.6';
      function val(id)  { var e = body.querySelector('#' + id); return e ? e.value.trim() : ''; }
      function chk(id)  { var e = body.querySelector('#' + id); return e && e.checked ? '✓' : '✗'; }
      function radio(n) { var e = body.querySelector('input[name="' + n + '"]:checked'); return e ? e.value : 'Not specified'; }
      function colors(slotId) {
        var vals = [];
        body.querySelectorAll('#' + slotId + ' [data-color-slot]').forEach(function (slot) {
          var sw = slot.querySelector('[data-color-value]');
          var v  = sw ? (sw.dataset.colorValue || '').trim() : '';
          if (v && v !== '#FFFFFF') vals.push(v);
        });
        return vals;
      }
      var lines = ['Service: ' + project];
      if (phone) lines.push('Phone: ' + phone);
      if (project === 'general') {
        lines.push('Deadline: ' + val('os9-contact-deadline'), 'Budget: ' + val('os9-contact-budget'), 'References: ' + val('os9-contact-refs'));
        var c = colors('os9-color-slots-general'); if (c.length) lines.push('Colors: ' + c.join(', '));
      } else if (project === 'event') {
        lines.push('Event Date: ' + val('os9-ev-date'), 'Flier Deadline: ' + val('os9-ev-deadline'), 'Venue: ' + val('os9-ev-venue'), 'Address: ' + val('os9-ev-address'), 'Door Cost: ' + val('os9-ev-door'), 'Ticket Link: ' + val('os9-ev-tickets'), 'Acts: ' + val('os9-ev-acts'));
        var c = colors('os9-color-slots-event'); if (c.length) lines.push('Colors: ' + c.join(', '));
      } else if (project === 'merch') {
        lines.push('Print Method: ' + radio('os9-merch-method'), 'Screen Ready: ' + chk('os9-merch-screen-ready'), 'Simulated Process: ' + chk('os9-merch-sim-process'), 'Location: ' + val('os9-merch-location'), 'Size: ' + val('os9-merch-size'), 'Deadline: ' + val('os9-merch-deadline'));
        var c = colors('os9-color-slots-merch'); if (c.length) lines.push('Colors: ' + c.join(', '));
      } else if (project === 'brand') {
        var svcs = ['rebrand','marketing','ads','other'].filter(function(s){var e=body.querySelector('#os9-brand-'+s);return e&&e.checked;}).map(function(s){return s.charAt(0).toUpperCase()+s.slice(1);});
        lines.push('Services: ' + (svcs.join(', ') || 'Not specified'));
      } else if (project === 'print') {
        lines.push('Type: ' + val('os9-print-type'), 'Dimensions: ' + val('os9-print-size'), 'Deadline: ' + val('os9-print-deadline'), 'Budget: ' + val('os9-print-budget'), 'References: ' + val('os9-print-refs'));
        var c = colors('os9-color-slots-print'); if (c.length) lines.push('Colors: ' + c.join(', '));
      } else if (project === 'web') {
        lines.push('Has Website: ' + radio('os9-web-existing'), 'Platform: ' + val('os9-web-platform'), 'Ecommerce: ' + radio('os9-web-ecomm'), 'Dropship: ' + radio('os9-web-drop'), 'Products/Services: ' + val('os9-web-products'), 'Budget: ' + val('os9-web-budget'));
      }
      if (message) lines.push('\nAdditional Notes: ' + message);
      /* CONTACT SUBMIT — native form + hidden iframe target
         Shopify's bot defense rejects an unprotected POST to /contact with
         HTTP 400, so the submission must carry a captcha token.

         Flow:
           1. Copy the OS9 UI values into the hidden #os9-real-contact-form
              fields (rendered by Liquid via {%- form 'contact' -%} in
              os9-desktop.liquid)
           2. Shopify.captcha.protect() attaches an invisible hCaptcha
           3. requestSubmit() fires a native submit; hCaptcha mints the token
              and posts it as h-captcha-response
           4. Shopify redirects back to ?contact_posted=true, which
              restoreContactSuccess() picks up on load

         An earlier version posted into a hidden iframe to avoid the page
         reload. That cannot work: hCaptcha routes the request through its own
         origin and the frame never returns to ours, so the result is
         unreadable. It "succeeded" unconditionally and delivered nothing. */
      try {
        var realForm = document.getElementById('os9-real-contact-form');
        if (!realForm) throw new Error('Real contact form not found in DOM (os9-desktop.liquid not rendered?)');

        var fullBody = lines.filter(Boolean).join('\n');
        var nameEl    = document.getElementById('os9-rcf-name');
        var emailEl   = document.getElementById('os9-rcf-email');
        var subjectEl = document.getElementById('os9-rcf-subject');
        var bodyEl    = document.getElementById('os9-rcf-body');
        if (!nameEl || !emailEl || !subjectEl || !bodyEl) {
          throw new Error('Real form fields missing — Liquid markup out of sync');
        }
        nameEl.value = name; emailEl.value = email;
        subjectEl.value = subject; bodyEl.value = fullBody;

        /* Bot protection is REQUIRED on this store: an unprotected POST to
           /contact returns HTTP 400 and nothing is delivered. This attaches an
           invisible hCaptcha; the token is minted during the native submit
           below and sent as h-captcha-response. */
        if (window.Shopify && window.Shopify.captcha && window.Shopify.captcha.protect) {
          await new Promise(function (res) {
            var once = false;
            function go() { if (!once) { once = true; res(); } }
            try { window.Shopify.captcha.protect(realForm, go); } catch (e) { go(); }
            setTimeout(go, 6000);
          });
        }

        /* Submit in the TOP window, not into a hidden iframe.

           The iframe approach could never be verified: hCaptcha routes the
           submission through its own origin and the frame never returns to
           ours, so contentDocument stays permanently unreadable. The old code
           papered over that by treating "no error found" as success — which is
           why the form reported "Message sent!" while delivering nothing.

           A real submit lets Shopify redirect back to ?contact_posted=true,
           which is an unambiguous, readable confirmation. restoreContactSuccess()
           picks that up on the next page load and shows the confirmation. */
        try { sessionStorage.setItem('os9-contact-sent-name', name.split(' ')[0] || ''); } catch (e) {}
        realForm.removeAttribute('target');
        realForm.requestSubmit ? realForm.requestSubmit() : realForm.submit();
        return;   /* page is navigating away */

        /* No local "Message sent!" here on purpose. The page navigates on
           submit, and confirmation comes from Shopify's ?contact_posted=true
           redirect via restoreContactSuccess(). Announcing success locally is
           precisely the bug this rewrite removed. */

      } catch (err) {
        console.error('[OS9 contact form] caught:', err);
        try { console.groupEnd(); } catch (e) {}
        sendBtn.textContent = 'Send'; sendBtn.style.opacity = '1';
        showDialog({
          title: 'Error',
          message: 'Could not send message. ' + (err && err.message ? '(' + err.message + ')' : 'Please try again.'),
          icon: '&#9888;', buttons: 'OK'
        });
      }

      /* ── ROLLBACK NOTE ───────────────────────────────────────────────
         Previous fetch('/contact') implementation lived here. Removed in
         favor of native form + hidden iframe because Shopify's bot defense
         rejects custom AJAX POSTs with "Missing CAPTCHA token" (400).
         To revert: `git revert <commit-hash>` rather than reinstating
         inline — the commit captures the full prior state cleanly.
         ──────────────────────────────────────────────────────────────── */
    });
  }

  /* ────────────────────────────────────────────────────────────
     CART WINDOW
  ──────────────────────────────────────────────────────────── */

  /* `nav` is the cart window's navigation stack (winState.nav). When present,
     a shop link drills down inside the cart window, Finder-style, so Back
     returns to the cart. It used to destroy the cart window and spawn a
     separate collection window, which is why there was no way back.
     Without nav (any other caller) it falls back to opening a window. */
  function wireShopLinks(body, nav) {
    body.querySelectorAll('.os9-cart-shop-link[data-handle]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var handle = link.dataset.handle;
        var title  = link.dataset.title || handle;
        if (nav && nav.openCollectionInPlace) { nav.openCollectionInPlace(handle, title); return; }
        var cartWin = document.getElementById('os9-cart-win');
        if (cartWin) cartWin.remove();
        openCollection({ handle: handle, title: title });
      });
    });
    body.querySelectorAll('.os9-cart-shop-link[data-commission]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        openPage({ url: '/pages/contact', title: 'Email Me' });
      });
    });
  }

  /* ── The Finder root ───────────────────────────────────────────────
     Squiche HD and Shop are the same view. This renders one listing that
     holds the cart, the collections, Games and Contact, using the Finder's
     own row classes so it is literally the same component the folder list
     uses — not a lookalike.

     Columns are Name / Qty / Kind. The kit's Size and Kind earn their place
     in a file browser; in a shop, price does — so Kind carries the price on
     anything that has one, and the kind word ("folder", "app", "page") on
     everything else. One column, because a row never needs both: a priced
     row is self-evidently an item, and the icon and chevron identify the
     rest. Qty is deliberately narrow — it holds a stepper and at most two
     digits. */

  var myItemsOpen = true;   /* "My Items" starts expanded */
  var gamesOpen   = false;  /* Games starts collapsed */

  /* Collections disclose their products inline, like every other folder here.
     Products are fetched on first expand and kept, so reopening a folder is
     instant and collapsing costs nothing. Keyed by collection handle. */
  var collectionsOpen  = {};
  var collectionCache  = {};
  /* Last click time per folder, keyed so it outlives the re-render that a
     toggle triggers. See wireFolder(). */
  var folderClickAt    = {};

  /* Warm the collection folders while the browser is idle.

     Opening one costs a products.json round trip — measured at 250-400ms on a
     desktop connection, which is the "Loading..." flash, and materially worse
     on a phone. The payload is only ~30 KB per collection, so fetching both
     shortly after the desktop settles buys an instant expand for well under
     what a single unsized thumbnail used to cost.

     Runs once per page, after first paint, and backs off entirely when the
     visitor has asked us not to spend their data. */
  /* ── Warming the next step's images ───────────────────────────────────
     Bandwidth is not the problem here. The CDN content-negotiates to WebP,
     so a 440px product image is about 40KB — nothing. Latency is: each one
     is a ~400ms cold round trip, and it is currently spent AFTER the click,
     with the visitor looking at an empty frame.

     So spend it earlier. Each stage of the journey warms the images the next
     stage will need, while the visitor is still reading the current one:

       collection expands  ->  warm every product's detail image
       product opens       ->  warm its full-size zoom
       browser dials up    ->  warm the page it is pretending to fetch

     Requests go through `new Image()`, so a warmed file is simply in the HTTP
     cache when the real <img> asks for it — no bookkeeping, no cache of our
     own to invalidate, and a miss costs nothing but the request we would have
     made anyway.

     Capped concurrency, because firing 28 at once just queues them behind
     each other and delays the one the visitor actually wants. */
  var WARM_MAX = 4;
  var _warmSeen = Object.create(null);
  var _warmQueue = [];
  var _warmActive = 0;

  function dataSaverOn() {
    var c = navigator.connection;
    return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
  }

  function warmImages(urls) {
    if (dataSaverOn()) return;
    for (var i = 0; i < urls.length; i++) {
      var u = urls[i];
      if (!u || _warmSeen[u]) continue;
      _warmSeen[u] = 1;
      _warmQueue.push(u);
    }
    pumpWarm();
  }

  function pumpWarm() {
    while (_warmActive < WARM_MAX && _warmQueue.length) {
      var url = _warmQueue.shift();
      _warmActive++;
      var img = new Image();
      img.onload = img.onerror = function () { _warmActive--; pumpWarm(); };
      /* Low priority so warming never competes with an image already on
         screen — the point is to use idle capacity, not to steal it. */
      if ('fetchPriority' in img) img.fetchPriority = 'low';
      img.decoding = 'async';
      img.src = url;
    }
  }

  /* Same idea for the product payload, but it has to be OUR cache, not the
     browser's. Opening a product needs /products/<handle>.js — the .json
     variant has no `available` field, so the size picker cannot be built from
     the collection data we already hold — and measuring shows that endpoint is
     served uncacheable: fetching it twice costs full price both times. Warming
     it into the HTTP cache therefore does nothing at all.

     So keep the parsed response in memory and have fetchProduct() read from
     there. Held for the life of the page only, which is the right lifetime for
     stock information. */
  var _productCache = Object.create(null);
  var _collectionCache = Object.create(null);

  function warmProducts(handles) {
    if (dataSaverOn()) return;
    handles.forEach(function (h) {
      if (!h || _productCache[h]) return;
      _productCache[h] = fetch('/products/' + h + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    });
  }

  /* Pull every usable image URL out of a products.json payload. */
  function productImageUrls(products, px) {
    var out = [];
    (products || []).forEach(function (p) {
      var src = p.images && p.images[0] && p.images[0].src;
      if (src) out.push(thumbUrl(src, px));
    });
    return out;
  }

  var prefetchStarted = false;
  function prefetchCollections(links) {
    if (prefetchStarted || !links || !links.length) return;
    prefetchStarted = true;

    var conn = navigator.connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;

    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1500); };
    idle(function () {
      links.forEach(function (sl) {
        if (collectionCache[sl.handle]) return;
        fetchCollection(sl.handle).then(function (products) {
          collectionCache[sl.handle] = products;
          /* Warm only the thumbnails that land above the fold on expand.
             They are ~2 KB each at this size; the rest stay lazy. */
          warmImages(productImageUrls(products.slice(0, 8), 64));
        }).catch(function () { /* a cold folder just loads on demand */ });
      });
    });
  }

  function money(cents) { return '$' + (cents / 100).toFixed(2); }

  function rootRow(opts) {
    var cls = 'finder-list-row' + (opts.nested ? ' is-nested' : '');
    return '<div class="' + cls + '"' + (opts.attrs || '') + '>' +
        '<div class="finder-lr-chev' + (opts.folder ? ' is-folder' : '') +
          (opts.open ? ' is-open' : '') + '">' + (opts.lead || '') + '</div>' +
        '<div class="finder-lr-icon">' + (opts.icon || '') + '</div>' +
        '<div class="finder-lr-name">' + opts.label + '</div>' +
        '<div class="finder-lr-size">' + (opts.qty || '--') + '</div>' +
        '<div class="finder-lr-kind">' + (opts.kind || '--') + '</div>' +
        '<div class="finder-lr-pad"></div>' +
      '</div>';
  }

  /* A nested row that stands in for content: "Empty" under a folder with
     nothing in it, "Loading..." while a collection's products are in flight. */
  function placeholderRow(label) {
    return '<div class="finder-list-row is-nested is-empty">' +
        '<div class="finder-lr-chev"></div>' +
        '<div class="finder-lr-icon"></div>' +
        '<div class="finder-lr-name">' + label + '</div>' +
        '<div class="finder-lr-size">--</div>' +
        '<div class="finder-lr-kind">--</div>' +
        '<div class="finder-lr-pad"></div>' +
      '</div>';
  }

  function renderCartBody(cart, body, infobar, nav) {
    if (window.OS9Finder && window.OS9Finder.injectCSS) window.OS9Finder.injectCSS();

    var items     = cart.items || [];
    var itemCount = cart.item_count || 0;
    var total     = money(cart.total_price || 0);

    infobar.textContent = itemCount + ' item' + (itemCount !== 1 ? 's' : '') + ' in cart, ' + total;
    syncInfobarState(infobar.closest('.os9-win') || infobar.parentElement);
    updateCartBadge(itemCount);
    updateTrashCount(itemCount);

    var folderIcon = '<div class="os9-cart-folder-icon"></div>';
    var rows = '';

    /* My Items — the cart, as an expandable folder. */
    rows += rootRow({
      label: 'My Items', folder: true, open: myItemsOpen, icon: folderIcon,
      qty: itemCount ? String(itemCount) : '--',
      kind: itemCount ? total : 'folder',
      attrs: ' data-toggle="my-items"',
    });

    if (myItemsOpen) {
      if (!items.length) rows += placeholderRow('Empty');
      items.forEach(function (item) {
        var wasHTML = '', offerHTML = '';
        if (item.original_line_price != null && item.original_line_price > item.final_line_price) {
          wasHTML = '<span class="os9-list__was">' + money(item.original_line_price) + '</span>';
          var names = (item.discounts || []).map(function (d) { return d.title; }).filter(Boolean);
          if (names.length) offerHTML = '<div class="os9-cart-item-offer">' + names.join(', ') + '</div>';
        }
        var thumb = item.featured_image && item.featured_image.url
          ? '<img class="os9-cart-thumb" src="' + thumbUrl(item.featured_image.url, 64) + '" alt="">'
          : '';
        var sub = (item.variant_title && item.variant_title !== 'Default Title')
          ? '<div class="os9-cart-item-sub">' + item.variant_title + '</div>' : '';

        rows += rootRow({
          nested: true,
          lead: '<div class="os9-cart-remove" title="Remove">&times;</div>',
          icon: thumb,
          label: '<div class="os9-cart-item-info"><div class="os9-cart-item-title">' +
                 item.product_title + '</div>' + sub + offerHTML + '</div>',
          qty: '<div class="os9-cart-qty-cell">' +
                 '<div class="os9-cart-qty-btn os9-qty-minus">&minus;</div>' +
                 '<div class="os9-cart-qty-val">' + item.quantity + '</div>' +
                 '<div class="os9-cart-qty-btn os9-qty-plus">+</div>' +
               '</div>',
          kind: wasHTML + money(item.final_line_price),
          attrs: ' data-key="' + item.key + '"',
        });
      });
    }

    /* Collections come from the theme editor's Cart Shop Link blocks. */
    var shopLinks = (window.OS9Config && window.OS9Config.shopLinks && window.OS9Config.shopLinks.length)
      ? window.OS9Config.shopLinks
      : [{ handle: 'squiche-art-prints', title: 'Art Prints' }, { handle: 'squiche-tees', title: 'Tee Shirt Designs' }];
    prefetchCollections(shopLinks);

    shopLinks.forEach(function (sl) {
      var cHandle = sl.handle;
      var cOpen   = !!collectionsOpen[cHandle];
      rows += rootRow({
        label: sl.title || cHandle, folder: true, open: cOpen, icon: folderIcon,
        /* Qty is the collection's product count, rendered into OS9Config by
           Liquid. The hardcoded fallback links carry no collection, so they
           have no count and fall through to "--". */
        qty: sl.count == null ? '--' : String(sl.count),
        kind: 'folder',
        attrs: ' data-toggle="collection" data-handle="' + cHandle + '" data-title="' +
               String(sl.title || cHandle).replace(/"/g, '&quot;') + '"',
      });

      if (!cOpen) return;

      var prods = collectionCache[cHandle];
      if (!prods) {
        rows += placeholderRow('Loading...');
      } else if (!prods.length) {
        rows += placeholderRow('Empty');
      } else {
        /* The row thumbnails are already loading; warm the detail image behind
           the first dozen, because opening one is the next thing that happens
           from here. Only the first dozen: warming all 28 of Art Prints costs
           about a megabyte to save a click that may never come, and the ones
           past the fold can wait until there is a reason to think they are
           wanted. */
        warmImages(productImageUrls(prods.slice(0, 12), 440));
        warmProducts(prods.slice(0, 12).map(function (p) { return p.handle; }));
        prods.forEach(function (p) {
          var img   = p.images && p.images[0] && p.images[0].src;
          var thumb = img
            ? '<img class="os9-cart-thumb" src="' + thumbUrl(img, 64) + '" alt="" loading="lazy">'
            : '';
          var price = p.variants && p.variants[0] ? formatPrice(p.variants[0].price) : '--';
          rows += rootRow({
            nested: true,
            icon: thumb,
            label: esc(p.title),
            kind: price,
            /* data-product-handle is what the window's existing delegated
               click handler listens for, so a product opens on a single tap
               with no extra wiring — and identically on mouse and touch. */
            attrs: ' data-product-handle="' + esc(p.handle) + '"',
          });
        });
      }
    });

    /* Games expands in place, the same way My Items does. The list is
       OS9Finder's own GAMES_ITEMS, so the apps and Flash games defined there
       show up here without a second copy to keep in sync. */
    var gamesList = (window.OS9Finder && window.OS9Finder.gamesItems)
      ? window.OS9Finder.gamesItems() : [];
    if (gamesList.length) {
      rows += rootRow({
        label: 'Games', folder: true, open: gamesOpen, icon: folderIcon,
        qty: String(gamesList.length),
        kind: 'folder',
        attrs: ' data-toggle="games"',
      });
      if (gamesOpen) {
        gamesList.forEach(function (g) {
          rows += rootRow({
            nested: true,
            icon: (window.OS9Finder.appIcon ? window.OS9Finder.appIcon(g, 16) : ''),
            label: g.label,
            kind: g.disabled ? 'coming soon' : 'app',
            attrs: ' data-game-id="' + g.id + '"',
          });
        });
      }
    }

    rows += rootRow({
      label: 'Contact',
      icon: '<div class="os9-cart-mail-icon"></div>',
      kind: 'page',
      attrs: ' data-commission="contact"',
    });

    var totalsHTML = '';
    if (items.length) {
      if ((cart.total_discount || 0) > 0) {
        totalsHTML += '<div class="os9-cart-total-row os9-cart-total-row--saved">' +
            '<div class="os9-cart-total-label">You save</div>' +
            '<div class="os9-cart-total-amount">&minus;' + money(cart.total_discount) + '</div>' +
          '</div>';
      }
      totalsHTML += '<div class="os9-cart-total-row">' +
          '<div class="os9-cart-total-label">Total</div>' +
          '<div class="os9-cart-total-amount">' + total + '</div>' +
        '</div>' +
        '<div class="os9-cart-checkout-bar">' +
          '<button class="os9-cart-checkout-btn" data-checkout>Checkout</button>' +
        '</div>';
    }

    body.innerHTML =
      '<div class="finder-list is-root">' +
        '<div class="finder-list-header">' +
          '<div class="finder-list-hcell finder-lh-chev"></div>' +
          '<div class="finder-list-hcell finder-lh-name is-active">Name</div>' +
          '<div class="finder-list-hcell finder-lh-size">Qty</div>' +
          '<div class="finder-list-hcell finder-lh-kind">Kind</div>' +
        '</div>' +
        '<div class="finder-list-body">' + rows + '</div>' +
      '</div>' + totalsHTML;

    /* ── Wiring ── */

    var toggle = body.querySelector('[data-toggle="my-items"]');
    if (toggle) toggle.addEventListener('click', function () {
      myItemsOpen = !myItemsOpen;
      renderCartBody(cart, body, infobar, nav);
    });

    /* Cart mutations are serialised through one promise chain. Two changes can
       be queued (remove one line while a quantity debounce is still pending)
       and they must reach Shopify in order, or the second overwrites the first
       with a stale quantity. */
    var cartQueue = Promise.resolve();

    function sendChange(key, qty) {
      cartQueue = cartQueue.then(async function () {
        try {
          var res = await fetch('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: qty }),
          });
          if (!res.ok) throw new Error('cart change failed');
          /* change.js already returns the complete updated cart. The old code
             discarded it and re-fetched the same data with /cart.js — a second
             round trip, measured at 171–216ms, for something we were handed. */
          renderCartBody(await res.json(), body, infobar, nav);
        } catch (err) {
          /* The optimistic edit was a guess and the server disagreed. Repaint
             from the authoritative cart so the row comes back. */
          renderCartBody(await fetchCart(), body, infobar, nav);
        }
      });
      return cartQueue;
    }

    body.querySelectorAll('.finder-list-row[data-key]').forEach(function (row) {
      var key = row.dataset.key;
      var line = items.filter(function (i) { return i.key === key; })[0];
      if (!line) return;

      var qtyVal  = row.querySelector('.os9-cart-qty-val');
      var optQty  = line.quantity;
      var qtyTimer = null;

      /* Everything below updates the row FIRST and talks to Shopify after.
         The round trip is 300–700ms on a good connection and several seconds
         on a bad one; blocking the UI on it made the × look broken, which is
         why people clicked it twice and sent two mutations. */
      function removeLine(e) {
        e.stopPropagation();
        if (row.dataset.removing) return;      /* ignore the second click */
        row.dataset.removing = '1';
        row.style.display = 'none';
        clearTimeout(qtyTimer);
        sendChange(key, 0);
      }

      function nudge(delta) {
        return function (e) {
          e.stopPropagation();
          if (row.dataset.removing) return;
          optQty = Math.max(0, optQty + delta);
          if (qtyVal) qtyVal.textContent = optQty;
          if (optQty === 0) { row.dataset.removing = '1'; row.style.display = 'none'; }
          /* Debounced: holding + is one request when the clicking stops, not
             one per click. */
          clearTimeout(qtyTimer);
          qtyTimer = setTimeout(function () { sendChange(key, optQty); }, 350);
        };
      }

      row.querySelector('.os9-cart-remove').addEventListener('click', removeLine);
      row.querySelector('.os9-qty-minus').addEventListener('click', nudge(-1));
      row.querySelector('.os9-qty-plus').addEventListener('click', nudge(1));
    });

    /* One tap discloses the folder in place; a double-click opens the folder's
       own full window. Touch only ever gets the first of those, which is why
       the disclosure has to be the single-tap action — iOS Safari does not
       fire dblclick dependably, and a double tap there is the zoom gesture.

       The second click of a double-click is swallowed rather than toggling
       the folder shut again, which keeps a single click instant instead of
       holding every toggle for 300ms to see whether a second one is coming.

       The timestamp MUST live outside the row. Toggling re-renders the whole
       listing, so the row this listener is bound to is destroyed by its own
       first click; a timestamp held in the listener's closure dies with it and
       the second click lands on a fresh row that has never been clicked. Keyed
       by folder here so it survives the re-render. */
    function wireFolder(row, key, toggle, openWindow) {
      row.addEventListener('click', function () {
        var now = Date.now();
        if (now - (folderClickAt[key] || 0) < 350) { folderClickAt[key] = 0; return; }
        folderClickAt[key] = now;
        toggle();
      });
      if (openWindow) row.addEventListener('dblclick', openWindow);
    }

    body.querySelectorAll('[data-toggle="collection"]').forEach(function (row) {
      var h = row.dataset.handle;
      wireFolder(row, 'collection:' + h, async function () {
        collectionsOpen[h] = !collectionsOpen[h];
        renderCartBody(cart, body, infobar, nav);   /* paint the open state now */
        if (!collectionsOpen[h] || collectionCache[h]) return;
        try {
          collectionCache[h] = await fetchCollection(h);
        } catch (e) {
          /* Leave the key unset so the next expand retries. Caching [] here
             would be truthy, so the guard above would short-circuit forever
             and the folder would claim to be empty for the rest of the
             session after one transient network failure. */
          delete collectionCache[h];
        }
        /* Only repaint if it is still open — the user may have collapsed it
           again while the products were in flight. */
        if (collectionsOpen[h]) renderCartBody(cart, body, infobar, nav);
      }, function () {
        if (nav && nav.openCollectionInPlace) {
          nav.openCollectionInPlace(h, row.dataset.title);
        }
      });
    });

    var gamesToggle = body.querySelector('[data-toggle="games"]');
    if (gamesToggle) wireFolder(gamesToggle, 'games', function () {
      gamesOpen = !gamesOpen;
      renderCartBody(cart, body, infobar, nav);
    }, function () {
      if (window.OS9Finder && window.OS9Finder.openGames) window.OS9Finder.openGames();
    });

    body.querySelectorAll('.finder-list-row[data-game-id]').forEach(function (row) {
      row.addEventListener('click', function () {
        if (window.OS9Finder && window.OS9Finder.openGameById) {
          window.OS9Finder.openGameById(row.dataset.gameId);
        }
      });
    });
    var contact = body.querySelector('[data-commission]');
    if (contact) contact.addEventListener('click', function () {
      openPage({ url: '/pages/contact', title: 'Email Me' });
    });

    var checkout = body.querySelector('[data-checkout]');
    if (checkout) checkout.addEventListener('click', function () { window.location.href = '/checkout'; });

    /* Single click selects, as in the Finder. */
    body.querySelectorAll('.finder-list-row').forEach(function (row) {
      row.addEventListener('click', function () {
        body.querySelectorAll('.finder-list-row').forEach(function (r) { r.classList.remove('is-selected'); });
        row.classList.add('is-selected');
      });
    });
  }

  /* The one shop window. Squiche HD, Trash and the Cart icon all land here;
     `root` only decides which frame you arrive on. The Finder is always the
     root of the stack, so Back from anywhere walks out to Squiche HD. */
  async function openCart({ x, y, title, root }) {
    /* Already open: focus it, and if it has been drilled down into a
       collection or product, walk back to the root rather than leaving
       the user staring at a product page after clicking the icon. */
    var open = document.getElementById('os9-cart-win');
    if (open) {
      var st = windows.get('os9-cart-win');
      var ws = st && st.winState;
      if (ws && ws.nav) {
        ws.nav.reset();
        if (root !== 'finder') { myItemsOpen = true; ws.nav.apply(ws.cartFrame); }
      }
    }
    if (bringOrOpen('cart')) return open;
    var b  = getDesktopBounds();
    var dw = b.width;
    var dh = b.height;
    var geom = shopWindowGeometry(b, x);
    var w    = geom.w;
    var h    = Math.min(400, dh - 60);
    var posX = geom.x;
    var posY = y !== undefined ? y : b.top + 10;
    var id = 'os9-cart-win';
    var el = buildWindow({ id, title: title || 'Cart', width: w, height: h, x: posX, y: posY });
    getLayer().appendChild(el);
    windows.set(id, { el });
    typeKeys.set('cart', id);
    initDrag(el); initResize(el); initMinimize(el);
    initClose(el, id, 'cart'); initZoom(el);
    watchScrollable(el.querySelector('.os9-win__scroll'));
    el.addEventListener('mousedown',  () => focusWindow(el));
    el.addEventListener('touchstart', () => focusWindow(el), { passive: true });
    var body    = el.querySelector('[data-body]');
    var infobar = el.querySelector('[data-infobar]');
    body.innerHTML = loadingHTML('Loading cart...');

    /* The cart window now uses the same navigation machinery as collection
       windows, so shop links can drill down into it and Back can walk out. */
    var winState = { products: null };
    windows.get(id).winState = winState;
    wireWindowContent(el, winState);
    var nav = winState.nav;

    /* One frame for both entry points. Squiche HD and Shop are the same
       view now, so there is no separate cart frame to navigate to — the
       cart is a folder inside this listing. */
    var cartFrame = {
      kind: 'finder', title: 'Squiche HD', infobar: '',
      render: async function () {
        try {
          var cart = await fetchCart();
          /* An entry point may navigate straight past this root (e.g. the
             browser's "Art Prints" link), and this fetch would otherwise
             resolve afterwards and paint over that collection. */
          if (winState.current !== cartFrame) return;
          renderCartBody(cart, body, infobar, nav);
        }
        catch { body.innerHTML = '<div class="os9-win__loading"><div>Could not load.</div></div>'; }
      },
    };

    /* Called after every Add to Cart. Repaint only when the cart itself is on
       screen — otherwise adding a tee while viewing that tee would replace the
       product page mid-action and throw the user out of the size picker. When
       drilled down we just refresh the counters, silently. */
    el._refreshCart = async function () {
      try {
        var cart = await fetchCart();
        updateCartBadge(cart.item_count);
        updateTrashCount(cart.item_count);
        if (nav.isShowing('finder')) renderCartBody(cart, body, infobar, nav);
      } catch (e) {}
    };

    /* ── Finder root — the top of this window's stack ──────────────────
       Renders the Squiche HD listing with the kit's Finder rules (see
       os9-finder.js). "My Items" is the cart, reachable here like any other
       folder, so the Finder really is the whole shopping surface. */
    /* Squiche HD and Shop resolve to the same frame. */
    var finderFrame = cartFrame;

    /* Keep a handle so a second click on the Cart icon can jump straight to
       the cart frame without rebuilding the window. */
    winState.cartFrame = cartFrame;

    /* Both entry points land on the same root; "Shop" simply arrives with
       My Items already expanded. */
    myItemsOpen = (root !== 'finder') ? true : myItemsOpen;
    nav.apply(cartFrame);
    return el;
  }

  /* ────────────────────────────────────────────────────────────
     INIT + EXPORT
  ──────────────────────────────────────────────────────────── */

  function init() {
    initDesktopBlur();
    fetchCart().then(function (cart) {
      updateCartBadge(cart.item_count);
      updateTrashCount(cart.item_count);
    }).catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ─────────────────────────────────────────────────────────────────────
     makeFullscreenOnMobile(el)
     Locks a window to fullscreen geometry between the OS9 menubar and
     statusbar on small viewports (width ≤ 749px). Tracks visualViewport
     resizes so the layout adapts when the iOS keyboard appears.
     Sets data-mobile-contact='1' which other window-manager paths treat
     as a signal to skip drag/clamp/etc.
     Returns true if applied, false if the viewport isn't mobile (no-op).
     Cleanup is automatic via initClose, which already removes
     el._mobileContactResizeHandler from visualViewport on window close.
     ───────────────────────────────────────────────────────────────────── */
  function makeFullscreenOnMobile(el) {
    if (!el || window.innerWidth > 749) return false;
    var vvp = window.visualViewport;
    var mb  = document.getElementById('os9-menubar');
    var sb  = document.querySelector('.os9-statusbar');
    var mbH = mb ? mb.offsetHeight : 22;
    var sbH = sb ? sb.offsetHeight : 24;
    /* Sizing only — z-index is deliberately NOT set here.

       This used to pin the window at z-index 99998 while the normal window
       stack lives around 1100. Any window that went fullscreen on mobile
       therefore sat ~98,000 levels above every other one and could never be
       covered: the browser calls this helper and the Finder does not, so on a
       phone Squiche HD could not be brought to the front no matter what, since
       focusWindow() only ever reaches ~1105. The sticky note lost the same way
       — it asks for _os9TopZ + 50.

       Going fullscreen is a statement about size, not about priority. The
       menubar (2147483000) and status bar (200) are outside the stack anyway,
       so a window in the 1100s still renders in the right place between them. */
    /* The windowed geometry to return to. Re-captured on every entry into
       fullscreen rather than once, so a window the visitor resized on desktop
       comes back at THAT size after a trip through phone width, not at
       whatever buildWindow() first gave it. */
    var saved = null;

    function release() {
      if (!saved) return;
      Object.assign(el.style, saved);
      saved = null;
      delete el.dataset.mobileContact;
      /* Listeners stay attached: this is a toggle, not a one-way door. A large
         phone in landscape is wider than 749, so rotating has to be able to
         drop fullscreen and rotating back has to restore it. */
      /* Hand it back to the normal resize logic, which has been skipping this
         window the whole time it was flagged mobileContact. */
      snapWindowsIntoView();
    }

    function apply() {
      /* Crossing back over the breakpoint has to UNDO fullscreen, not re-apply
         it at the new size. Without this a window opened while the viewport was
         narrow stayed position:fixed and full-bleed forever — widening a desktop
         browser just re-fullscreened it, and snapWindowsIntoView() could never
         reclaim it because it skips [data-mobile-contact]. Fullscreen is a
         response to the current viewport, not a permanent mode. */
      if (window.innerWidth > 749) { release(); return; }
      if (!saved) {
        saved = {
          position: el.style.position, left: el.style.left, top: el.style.top,
          width: el.style.width, height: el.style.height,
        };
        el.dataset.mobileContact = '1';
      }
      var vp   = window.visualViewport;
      var vpW  = vp ? vp.width     : window.innerWidth;
      var vpH  = vp ? vp.height    : window.innerHeight;
      var offT = vp ? vp.offsetTop : 0;
      Object.assign(el.style, {
        position: 'fixed', left: '0px', top: (offT + mbH) + 'px',
        width: vpW + 'px', height: (vpH - mbH - sbH) + 'px',
      });
    }
    apply();
    /* Raised once, through the normal stack, so it opens on top like any other
       window and can then be covered normally. Not inside apply(), which re-runs
       on every visualViewport resize — bumping the z-counter there would let a
       background window steal focus every time the keyboard opened. */
    focusWindow(el);
    /* mobileContact is set and cleared by apply()/release() now, so it always
       reflects whether the window is ACTUALLY fullscreen rather than just
       whether this helper once ran. */
    el._mobileContactResizeHandler = apply;
    if (vvp) vvp.addEventListener('resize', apply);
    /* Dragging a desktop window across the breakpoint fires window resize but
       not always visualViewport resize, so both are needed for release() to
       run reliably. */
    window.addEventListener('resize', apply);
    return true;
  }

  /* Shopify redirects back to <page>?contact_posted=true after a successful
     contact submission — the only unambiguous delivery confirmation available
     to the storefront. Pick it up on load, tell the visitor, and tidy the URL
     so a refresh doesn't re-announce it. */
  function restoreContactSuccess() {
    if (window.location.search.indexOf('contact_posted=true') === -1) return;
    var who = '';
    try {
      who = sessionStorage.getItem('os9-contact-sent-name') || '';
      sessionStorage.removeItem('os9-contact-sent-name');
    } catch (e) {}
    try {
      var u = new URL(window.location.href);
      u.searchParams.delete('contact_posted');
      window.history.replaceState({}, '', u.pathname + (u.search || '') + (u.hash === '#os9-real-contact-form' ? '' : u.hash));
    } catch (e) {}
    setTimeout(function () {
      showDialog({
        title: 'Message sent',
        message: 'Thanks' + (who ? ' ' + who : '') + ', your message went through. I\'ll be in touch soon.',
        icon: '&#10003;', buttons: 'OK'
      });
    }, 600);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreContactSuccess);
  } else {
    restoreContactSuccess();
  }

  window.OS9Windows = {
    openCollection,
    openPage,
    openCart,
    /* Squiche HD / Trash entry point — same window, rooted at the Finder. */
    openFinder: function (opts) { return openCart(Object.assign({}, opts || {}, { root: 'finder' })); },
    /* Open the Finder on one product, by handle. */
    openProduct: async function (opts) {
      opts = opts || {};
      const el = await openCart({ x: opts.x, y: opts.y, root: 'finder' });
      const st = windows.get('os9-cart-win');
      const nav = st && st.winState && st.winState.nav;
      if (nav && opts.handle) nav.openProductInPlace(opts.handle);
      return el;
    },
    showDialog,
    focusWindow,
    blurAllWindows,
    /* Exported so the browser's page divs get the same per-axis scrollable
       classes as .os9-win__scroll — without them their arrows never grey. */
    watchScrollable,
    /* Shared image warmer — the browser uses it to spend its fake dial-up
       delay on real work. */
    warmImages,
    thumbUrl,
    bringOrOpen,
    getDesktopBounds,
    getLayer,
    enterKeyboardMode,
    exitKeyboardMode,
    syncKeyboardModeTop,
    syncMobileContactTop,
    makeFullscreenOnMobile,
    _buildWindow:      buildWindow,
    _initWindow:       _initWindow,
    _registerTypeKey:  function (type, id) { typeKeys.set(type, id); },
  };

})();